import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Users, Search, Shield, Stethoscope, UserCog, Pill, ClipboardList,
  X, Mail, Phone, Copy, CheckCircle, AlertCircle,
  Edit2, RefreshCw, Key, UserPlus,
  BadgeCheck, Clock, Filter, ChevronDown, Sparkles, Lock, User as UserIcon,
  ChevronUp, Power, Plus, FileSignature, ChevronsDownUp, ChevronsUpDown,
  Calendar, FileText, Eye, Globe, Briefcase,
  GraduationCap, Image as ImageIcon, Trash2, ChevronRight, Paperclip, CalendarClock
} from 'lucide-react';
import { userAPI, rolePermissionAPI, medicalServiceAPI, specialityAPI } from '../services/api';
import CountryCodeSelect from './CountryCodeSelect';
import usePhoneCountryCode from '../hooks/usePhoneCountryCode';
import DatePicker from './DatePicker';
import SearchableSelect from './SearchableSelect';
import ProfilePicturePicker from './ProfilePicturePicker';
import { useToast, useConfirm } from './UIProvider';
import { buildPhone, parsePhone, validatePhone, getPhoneLength } from '../utils/phone';
import { formatClinicDate, formatClinicDateTime, clinicToday } from '../utils/clinicTime';
import {
  CreateRoleModal,
  MODULE_CONFIG as PERM_MODULE_CONFIG,
  ACTION_CONFIG as PERM_ACTION_CONFIG,
  GROUP_LABEL as PERM_GROUP_LABEL,
  BASE_ACTIONS as PERM_BASE_ACTIONS,
  MODULE_GROUPS as PERM_MODULE_GROUPS,
  fallbackActionConfig as permFallbackActionConfig,
} from './RolesPermissions';
import { HIDDEN_PERMISSION_MODULES, HIDDEN_PERMISSION_ACTIONS } from '../config/features';
import { toTitleCase } from '../utils/text';
import { handleCnicInput } from '../utils/cnic';
import { groupDigits, handleAmountInput } from '../utils/money';
import { authedMediaUrl } from '../utils/media';
import { onActivateKey, ROW_FOCUS_RING } from '../utils/keyboard';
import { FilePreviewEye, AvatarHoverPreview } from './MediaPreview';
import ImageLightbox from './ImageLightbox';
import CnicDocUpload from './CnicDocUpload';
import Select from './Select';
import TablePager, { usePagedRows } from './TablePager';
import WorkingHoursEditor from './WorkingHoursEditor';
import { parseTimings, serializeTimings } from '../utils/doctorTimings';
import useDialog from '../hooks/useDialog';

// Icon name → component mapping for dynamic roles
const ICON_MAP = { Shield, Stethoscope, UserCog, Pill, ClipboardList, Users: UserIcon };

// Generate color styles from a color key
const COLOR_MAP = {
  red:     { color: 'from-red-500 to-rose-600', bgLight: 'bg-red-50', textColor: 'text-red-700', borderColor: 'border-red-200', badgeBg: 'bg-red-100 text-red-700' },
  blue:    { color: 'from-blue-500 to-indigo-600', bgLight: 'bg-blue-50', textColor: 'text-blue-700', borderColor: 'border-blue-200', badgeBg: 'bg-blue-100 text-blue-700' },
  purple:  { color: 'from-purple-500 to-violet-600', bgLight: 'bg-purple-50', textColor: 'text-purple-700', borderColor: 'border-purple-200', badgeBg: 'bg-purple-100 text-purple-700' },
  emerald: { color: 'from-emerald-500 to-teal-600', bgLight: 'bg-emerald-50', textColor: 'text-emerald-700', borderColor: 'border-emerald-200', badgeBg: 'bg-emerald-100 text-emerald-700' },
  amber:   { color: 'from-amber-500 to-orange-600', bgLight: 'bg-amber-50', textColor: 'text-amber-700', borderColor: 'border-amber-200', badgeBg: 'bg-amber-100 text-amber-700' },
  gray:    { color: 'from-gray-500 to-gray-600', bgLight: 'bg-gray-50', textColor: 'text-gray-700', borderColor: 'border-gray-200', badgeBg: 'bg-gray-100 text-gray-700' },
  teal:    { color: 'from-teal-500 to-cyan-600', bgLight: 'bg-teal-50', textColor: 'text-teal-700', borderColor: 'border-teal-200', badgeBg: 'bg-teal-100 text-teal-700' },
  pink:    { color: 'from-pink-500 to-rose-600', bgLight: 'bg-pink-50', textColor: 'text-pink-700', borderColor: 'border-pink-200', badgeBg: 'bg-pink-100 text-pink-700' },
  indigo:  { color: 'from-indigo-500 to-blue-600', bgLight: 'bg-indigo-50', textColor: 'text-indigo-700', borderColor: 'border-indigo-200', badgeBg: 'bg-indigo-100 text-indigo-700' },
};

/** Build ROLE_CONFIG from API roles list */
const buildRoleConfig = (roles) => {
  const config = {};
  (roles || []).forEach(r => {
    const colors = COLOR_MAP[r.color] || COLOR_MAP.gray;
    config[r.name] = {
      label: r.label,
      icon: ICON_MAP[r.icon] || UserCog,
      description: r.description || '',
      ...colors,
    };
  });
  return config;
};

const STATUS_CONFIG = {
  ACTIVE: { label: 'Active', color: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
  INACTIVE: { label: 'Inactive', color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
  SUSPENDED: { label: 'Suspended', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  // Soft-deleted users are hidden from the default list (server filters them out unless
  // the status=DELETED filter is explicitly selected). Rendered with a muted slate tone
  // so they don't look like an active status when browsing the Deleted view.
  DELETED: { label: 'Deleted', color: 'bg-slate-200 text-slate-700', dot: 'bg-slate-500' },
};

// Specialities are no longer hardcoded here — they come from the managed catalog
// (Settings → Specialities) so they can also scope the prescription instruction chips
// and quick lab tests.

// Common languages in Pakistan. Multi-select on the Personal section (any role).
// English and Urdu are by far the most common languages in this clinic, so they're
// rendered as always-visible toggle pills. The rest live behind a "+ More" dropdown so
// the form doesn't get cluttered for the 95% case.
const PRIMARY_LANGUAGES = ['English', 'Urdu'];
const OTHER_LANGUAGES = ['Arabic', 'Balochi', 'Pashto', 'Punjabi', 'Saraiki', 'Sindhi'];
const LANGUAGE_OPTIONS = [...PRIMARY_LANGUAGES, ...OTHER_LANGUAGES];

// Account status options shown in the Employment section. Maps directly to the backend
// User.UserStatus enum.
const ACCOUNT_STATUS_OPTIONS = [
  { value: 'ACTIVE',    label: 'Active' },
  { value: 'INACTIVE',  label: 'Inactive' },
  { value: 'SUSPENDED', label: 'Suspended' },
];

// Sensible per-role designation defaults. Free text in the field if none match — the
// dropdown's "Other (type below)" option flips it into a text input. Custom (non-system)
// roles fall back to the generic list.
const DESIGNATIONS_BY_ROLE = {
  ADMIN:        ['System Administrator', 'IT Administrator', 'Senior Admin'],
  ACCOUNTS:     ['Accountant', 'Senior Accountant', 'Accounts Manager', 'Finance Officer', 'Cashier'],
  DOCTOR:       ['Consultant', 'Senior Consultant', 'Resident', 'Junior Doctor', 'Medical Officer', 'House Officer'],
  NURSE:        ['Staff Nurse', 'Senior Nurse', 'Head Nurse', 'Charge Nurse', 'Nursing Supervisor', 'Midwife'],
  PHARMACIST:   ['Pharmacist', 'Senior Pharmacist', 'Chief Pharmacist', 'Pharmacy Technician'],
  DISPENSER:    ['Dispenser', 'Senior Dispenser', 'Pharmacy Assistant', 'Counter Assistant'],
  RECEPTIONIST: ['Receptionist', 'Senior Receptionist', 'Front Desk Officer', 'Front Desk Manager'],
  TECHNICIAN:   ['Lab Technician', 'Senior Lab Technician', 'Radiology Technician', 'OT Technician', 'Maintenance Technician'],
};
const DESIGNATION_GENERIC = ['Officer', 'Senior Officer', 'Manager', 'Supervisor', 'Coordinator', 'Assistant'];
const designationsFor = (role) => DESIGNATIONS_BY_ROLE[role] || DESIGNATION_GENERIC;

// Section wrapper used inside AddUserModal. Accordion-controlled: the parent owns which
// section is open and passes it in via `open`. `onToggle` flips the parent state. Falls
// back to a self-managed `defaultOpen` for any caller that doesn't wire the controlled
// API — keeps the component drop-in for any non-accordion use.
const Section = ({ title, icon: Icon, children, defaultOpen = false, open, onToggle }) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = typeof open === 'boolean';
  const isOpen = isControlled ? open : internalOpen;
  const handleToggle = () => {
    if (isControlled) onToggle && onToggle();
    else setInternalOpen(o => !o);
  };
  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center gap-2 text-left hover:opacity-80 transition-opacity"
      >
        {Icon && <Icon className="h-4 w-4 text-emerald-600" />}
        <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-700">{title}</h3>
        <div className="flex-1 h-px bg-gray-200" />
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && <div className="space-y-4">{children}</div>}
    </div>
  );
};

// Languages picker — two always-visible toggle pills for the common languages
// (English / Urdu) plus a "+ More" dropdown for the rest. Selected "other" languages
// render as removable chips between the pills and the dropdown so the user can see
// everything they've picked at a glance.
const LanguagesPicker = ({ selected = [], onChange }) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!moreOpen) return;
    const onMouse = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setMoreOpen(false);
    };
    // Capture phase, stopped: this Escape closes the list and not the Add User dialog around it.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setMoreOpen(false);
    };
    document.addEventListener('mousedown', onMouse, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onMouse, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [moreOpen]);

  const toggle = (lang) => {
    onChange(selected.includes(lang) ? selected.filter(l => l !== lang) : [...selected, lang]);
  };

  const extraSelected = selected.filter(l => !PRIMARY_LANGUAGES.includes(l));

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">Languages Spoken</label>
      <div className="flex flex-wrap items-center gap-2" ref={wrapperRef}>
        {PRIMARY_LANGUAGES.map(lang => {
          const on = selected.includes(lang);
          return (
            <button
              key={lang}
              type="button"
              onClick={() => toggle(lang)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                on
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'
              }`}
            >
              {lang}
            </button>
          );
        })}
        {/* Extra languages picked from the dropdown — shown as solid chips with an X. */}
        {extraSelected.map(lang => (
          <span
            key={lang}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-600 text-white border border-emerald-600"
          >
            {lang}
            <button
              type="button"
              onClick={() => toggle(lang)}
              className="hover:bg-white/20 rounded-full p-0.5"
              title="Remove"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {/* + More button. The dropdown lists OTHER_LANGUAGES as checkboxes (excluding
            already-selected ones, which appear as chips above). Click outside closes. */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setMoreOpen(o => !o)}
            className="px-3 py-1.5 rounded-full text-xs font-medium border-2 border-dashed border-gray-300 text-gray-600 hover:border-emerald-400 hover:bg-emerald-50 inline-flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> More
          </button>
          {moreOpen && (
            <div className="absolute z-30 mt-1 right-0 w-52 bg-white border border-gray-200 rounded-lg shadow-xl py-1">
              {/* Header with explicit Done — click-outside / Escape also close, but a
                  visible affordance here means the user is never stranded with no way
                  to dismiss. */}
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">More languages</span>
                <button
                  type="button"
                  onClick={() => setMoreOpen(false)}
                  className="text-xs font-medium text-emerald-700 hover:text-emerald-800"
                  title="Done (Esc)"
                >
                  Done
                </button>
              </div>
              {OTHER_LANGUAGES.map(lang => {
                const on = selected.includes(lang);
                return (
                  <label
                    key={lang}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer ${
                      on ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(lang)}
                      className="rounded border-gray-300 focus:ring-emerald-500"
                    />
                    {lang}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Circular profile-picture picker — click the circle to upload, click X to clear.
// Previews the chosen file via an object URL. Optional everywhere it's used; the actual
// upload happens after the user row is created (via /api/users/{id}/profile-image).
// Lightweight file picker — shows filename + size when a file is selected, with a clear
// button. Reused for PMDC certificate and Resume in the Doctor profile section.
// Label + control sized to match the regular `<input>` styling used elsewhere in the
// modal (font-semibold / mb-2 / p-3.5 / border-2 / rounded-xl) so FileInput sitting in
// the same grid row lines up flush with the inputs next to it.
const FileInput = ({ label, accept, file, onChange, error, hint }) => {
  const inputRef = useRef(null);
  const sizeKb = file ? Math.round(file.size / 1024) : 0;
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
      {file ? (
        <div className="flex items-center gap-2 p-3.5 bg-white border-2 border-emerald-200 rounded-xl">
          <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate" title={file.name}>{file.name}</div>
            <div className="text-xs text-gray-500">{sizeKb} KB</div>
          </div>
          <button
            type="button"
            onClick={() => { onChange(null); if (inputRef.current) inputRef.current.value = ''; }}
            className="text-gray-400 hover:text-red-500"
            title="Remove"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`w-full flex items-center gap-2 p-3.5 bg-white border-2 border-dashed rounded-xl text-sm text-gray-600 transition-colors hover:bg-emerald-50 ${
            error ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-emerald-400'
          }`}
        >
          <UserPlus className="h-4 w-4 text-gray-400" />
          <span>Click to upload</span>
        </button>
      )}
      {hint && !error && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

// ViewUserModal helpers — categorized card wrapper, label/value row, and document tile.
// Mirrors the visual language of PatientManagement.PatientDetailsModal (gradient header,
// rounded category cards, white-tinted rows) so the two modals feel like one product.

// `wide` makes the card span both columns of the outer 2-col grid and lays its rows out
// in a 2-col inner grid — useful for the Professional Details card which otherwise leaves
// half the modal as empty white space. Child elements that should span the full inner
// width (e.g. a Services Provided table) can opt in with `lg:col-span-2`.
const UserInfoCard = ({ title, icon: Icon, gradient, border, iconBg, wide = false, children }) => (
  <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-6 border ${border} shadow-lg ${wide ? 'lg:col-span-2' : ''}`}>
    <div className="flex items-center space-x-3 mb-6">
      <div className={`w-12 h-12 bg-gradient-to-br ${iconBg} rounded-xl flex items-center justify-center`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <h3 className="text-2xl font-bold text-gray-800">
        {title}
      </h3>
    </div>
    <div className={wide ? 'grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-4' : 'space-y-4'}>
      {children}
    </div>
  </div>
);

// Falsy values render as the muted "Not provided" placeholder so the cards always show
// the same row count regardless of how much profile data the user has on file.
const UserRow = ({ icon, label, value, mono = false, highlight = false }) => {
  const isEmpty = value == null || value === '';
  return (
    <div className={`flex items-center justify-between gap-4 p-3.5 rounded-xl transition-all duration-200 hover:shadow-sm ${
      highlight ? 'bg-white bg-opacity-80 shadow-sm' : 'bg-white bg-opacity-40'
    }`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex-shrink-0">{icon}</div>
        <span className="text-sm font-medium text-gray-600 whitespace-nowrap">{label}</span>
      </div>
      <span className={`font-semibold text-sm text-right break-words ${mono ? 'font-mono' : ''} ${
        isEmpty ? 'text-gray-400 italic font-normal' : 'text-gray-900'
      }`}>
        {isEmpty ? 'Not provided' : value}
      </span>
    </div>
  );
};

// ============ ACTIVITY TIMELINE ============
// Renders the audit log fetched from GET /api/users/{id}/audit-log. Each entry is a
// timeline row with an action-specific icon + colour, a one-line headline, an old → new
// diff (for field edits), a reason badge + description block (for status changes), and
// who-by / when in muted text on the right.

// Per-action presentation. Drives the icon, the dot colour on the timeline rail, and
// the headline verb used when rendering the row.
const AUDIT_ACTION_META = {
  USER_CREATED:   { icon: UserPlus,      dot: 'bg-emerald-500', text: 'text-emerald-600', label: 'User created' },
  STATUS_CHANGED: { icon: Power,         dot: 'bg-amber-500',   text: 'text-amber-600',   label: 'Status changed' },
  FIELD_UPDATED:  { icon: Edit2,         dot: 'bg-sky-500',     text: 'text-sky-600',     label: 'Profile updated' },
  PASSWORD_RESET: { icon: Key,           dot: 'bg-violet-500',  text: 'text-violet-600',  label: 'Password reset' },
  USER_DELETED:   { icon: Trash2,        dot: 'bg-red-500',     text: 'text-red-600',     label: 'User deleted' },
};

// Human label for the field-name string the backend persists. Falls back to the raw
// name if a new field is added without updating the map.
const FIELD_LABELS = {
  status: 'Status',
  email: 'Email',
  firstName: 'First name',
  lastName: 'Last name',
  phone: 'Phone',
  role: 'Role',
  specialization: 'Specialization',
  practiceLocation: 'Practice location',
  twoFactorEnabled: 'Two-factor auth',
  title: 'Title',
  dateOfBirth: 'Date of birth',
  cnic: 'CNIC',
  languagesSpoken: 'Languages',
  designation: 'Designation',
  qualification: 'Qualification',
  qualificationDateCompleted: 'Qualification date',
  qualificationDescription: 'Qualification description',
  salary: 'Salary',
  pmdcNumber: 'PMDC number',
  pmdcIssueDate: 'PMDC issue date',
  pmdcExpiryDate: 'PMDC expiry date',
  practiceType: 'Practice type',
  practitionerType: 'Practitioner type',
};

// Field-name → human label, falling back to the raw name for anything not mapped above.
const fieldLabelFor = (name) => (name ? (FIELD_LABELS[name] || name) : null);

// Fields whose stored value is an ISO date (YYYY-MM-DD). These get formatted to the app's
// standard "1 June 2026" date format instead of being shown raw, to match the rest of the UI.
const DATE_VALUE_FIELDS = new Set([
  'dateOfBirth', 'pmdcIssueDate', 'pmdcExpiryDate', 'qualificationDateCompleted',
]);

// Render an audit old/new value: italic "empty" for blanks, app-formatted dates for date
// fields, raw text otherwise.
const formatAuditValue = (v, fieldName) => {
  if (v == null || v === '') return <span className="text-gray-400 italic">empty</span>;
  if (fieldName && DATE_VALUE_FIELDS.has(fieldName) && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    const d = new Date(v);
    if (!isNaN(d.getTime())) {
      return formatClinicDate(v, { day: 'numeric', month: 'long', year: 'numeric' });
    }
  }
  return v;
};

// Collapse the flat audit log into display groups: FIELD_UPDATED rows from one save become a
// single grouped entry holding all the field changes; everything else (status change, create,
// password reset) stays standalone. The log is newest-first and rows from one save are contiguous.
//
// Grouping signal, in priority order:
//   1. batchId — authoritative; written for every multi-field save (see UserService.updateUser).
//   2. Legacy fallback for rows with NO batchId (written before batching existed): same actor +
//      timestamps within a few seconds of each other → treated as one transaction. Without this,
//      historical edits would still show as one card per field.
const LEGACY_BATCH_WINDOW_MS = 5000;
const groupAuditLog = (log) => {
  const groups = [];
  for (const entry of log) {
    if (entry.action !== 'FIELD_UPDATED') {
      groups.push({ key: entry.id, grouped: false, entry });
      continue;
    }
    const prev = groups[groups.length - 1];
    let sameSave = false;
    if (prev && prev.grouped) {
      if (entry.batchId && prev.batchId) {
        sameSave = entry.batchId === prev.batchId;
      } else if (!entry.batchId && !prev.batchId) {
        // Legacy rows: group consecutive same-actor changes that are seconds apart.
        const last = prev.changes[prev.changes.length - 1];
        const gap = Math.abs(new Date(entry.createdAt).getTime() - new Date(last.createdAt).getTime());
        sameSave = entry.changedByName === prev.changedByName && gap <= LEGACY_BATCH_WINDOW_MS;
      }
    }
    if (sameSave) {
      prev.changes.push(entry);
    } else {
      groups.push({
        key: entry.batchId || entry.id,
        grouped: true,
        action: 'FIELD_UPDATED',
        batchId: entry.batchId || null,
        createdAt: entry.createdAt,
        changedByName: entry.changedByName,
        changes: [entry],
      });
    }
  }
  return groups;
};

const ActivityTimeline = ({ log, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="h-6 w-6 text-emerald-500 animate-spin" />
      </div>
    );
  }
  if (!log || log.length === 0) {
    return (
      <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
        <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium">No activity yet</p>
        <p className="text-gray-400 text-sm mt-1">
          Profile changes, status flips, password resets, and other actions will show up here.
        </p>
      </div>
    );
  }
  // Timeline timestamp — matches the app's standard date-time format (same options as
  // ViewUserModal.formatDateTime): "1 Jun 2026, 16:01".
  const formatWhen = (createdAt) => formatClinicDateTime(createdAt);

  // The right-hand who/when block, shared by single and grouped cards.
  const ByLine = ({ createdAt, changedByName }) => (
    <div className="text-right shrink-0">
      <p className="text-xs text-gray-500">{formatWhen(createdAt)}</p>
      {changedByName && (
        <p className="text-xs text-gray-700 font-medium mt-0.5">{changedByName}</p>
      )}
    </div>
  );

  // The old → new value pills for one field change.
  const Diff = ({ entry }) => (
    <div className="flex items-center flex-wrap gap-2 text-sm">
      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md font-mono text-xs">
        {formatAuditValue(entry.oldValue, entry.fieldName)}
      </span>
      <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
      <span className={`px-2 py-0.5 rounded-md font-mono text-xs ${
        entry.action === 'STATUS_CHANGED' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
      }`}>
        {formatAuditValue(entry.newValue, entry.fieldName)}
      </span>
    </div>
  );

  const groups = groupAuditLog(log);

  return (
    <div className="relative">
      {/* Vertical rail behind the dots — gives the list a timeline feel. */}
      <div className="absolute left-[19px] top-2 bottom-2 w-px bg-gray-200" aria-hidden="true" />
      <div className="space-y-4">
        {groups.map(group => {
          // ---- Grouped multi-field edit: one card, every changed field listed inside. ----
          if (group.grouped) {
            const meta = AUDIT_ACTION_META.FIELD_UPDATED;
            const Icon = meta.icon;
            const n = group.changes.length;
            return (
              <div key={group.key} className="relative flex gap-4">
                <div className="relative flex-shrink-0 z-10">
                  <div className={`w-10 h-10 rounded-full ${meta.dot} bg-opacity-10 border-2 border-white shadow-sm flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${meta.text}`} />
                  </div>
                </div>
                <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-semibold text-gray-900">
                      {meta.label}
                      <span className="text-gray-500 font-normal"> · {n} field{n !== 1 ? 's' : ''}</span>
                    </p>
                    <ByLine createdAt={group.createdAt} changedByName={group.changedByName} />
                  </div>
                  {/* One row per changed field. */}
                  <div className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
                    {group.changes.map(change => (
                      <div key={change.id} className="py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
                        <span className="text-sm font-medium text-gray-700">{fieldLabelFor(change.fieldName)}</span>
                        <Diff entry={change} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          }

          // ---- Standalone entry: status change, create, password reset, legacy row, etc. ----
          const entry = group.entry;
          const meta = AUDIT_ACTION_META[entry.action]
            || { icon: ClipboardList, dot: 'bg-gray-400', text: 'text-gray-600', label: entry.action };
          const Icon = meta.icon;
          const fieldLabel = fieldLabelFor(entry.fieldName);
          return (
            <div key={group.key} className="relative flex gap-4">
              <div className="relative flex-shrink-0 z-10">
                <div className={`w-10 h-10 rounded-full ${meta.dot} bg-opacity-10 border-2 border-white shadow-sm flex items-center justify-center`}>
                  <Icon className={`h-5 w-5 ${meta.text}`} />
                </div>
              </div>
              <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900">
                      {meta.label}
                      {fieldLabel && entry.action === 'FIELD_UPDATED' && (
                        <span className="text-gray-500 font-normal"> · {fieldLabel}</span>
                      )}
                    </p>
                    {(entry.oldValue != null || entry.newValue != null) && (
                      <div className="mt-2"><Diff entry={entry} /></div>
                    )}
                    {entry.reason && (
                      <div className="mt-2">
                        <span className="inline-block px-2.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium">
                          Reason: {entry.reason}
                        </span>
                      </div>
                    )}
                    {entry.description && (
                      <p className="mt-2 text-sm text-gray-600 italic whitespace-pre-wrap break-words">
                        {entry.description}
                      </p>
                    )}
                  </div>
                  <ByLine createdAt={entry.createdAt} changedByName={entry.changedByName} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const UserManagement = ({ hasPermission }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('USER_MANAGEMENT', action) : true),
    [hasPermission],
  );
  // The signed-in user. User Management is for other people's accounts: the server refuses resetting,
  // suspending, deleting or re-permissioning your own, so those buttons are not shown on your row.
  const myId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('clinic_auth_user') || '{}').id || null;
    } catch {
      return null;
    }
  }, []);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUserCredentials, setNewUserCredentials] = useState(null);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('fullName');
  const [sortDir, setSortDir] = useState('asc');
  const [roles, setRoles] = useState([]);
  const [permEditUser, setPermEditUser] = useState(null);
  // Open status-change dialog: { user, action } | null. action ∈ {'activate',
  // 'deactivate', 'delete', 'restore'}. Drives the modal that captures reason +
  // description before flipping the user's status (or restoring a deleted one).
  const [statusChange, setStatusChange] = useState(null);
  const ROLE_CONFIG = buildRoleConfig(roles);

  useEffect(() => {
    // loadUsers runs via the dedicated effect below (re-fires on statusFilter changes).
    // Only fetch the roles list here.
    rolePermissionAPI.getRoles().then(data => setRoles(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  // Always load ALL users — filtering + stats are derived client-side from this full list.
  // Never send filter params to the backend, or stats will recompute from a subset and show
  // wrong counts (the same "single source of truth" pattern used in Medicines/Vendors/Assets).
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Pass status to backend so the "Deleted" filter can fetch soft-deleted users —
      // those are hidden by default server-side. For ALL, the backend omits DELETED.
      const filters = statusFilter !== 'ALL' ? { status: statusFilter } : {};
      const response = await userAPI.getAll(filters);
      setUsers(response || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users. Please check if backend is running.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Reload from the backend whenever the status filter changes so the Deleted view
  // gets its data. Other filters (role, search) are applied client-side.
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm ||
      user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.includes(searchTerm);
    const matchesRole = roleFilter === 'ALL' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'ALL' || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    if (sortField === 'createdAt') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
  const paging = usePagedRows(sortedUsers, {
    id: 'users',
    resetOn: [searchTerm, roleFilter, statusFilter, sortField, sortDir],
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Search is client-side — no need to call loadUsers
  const handleSearch = () => {};

  // Edit-target user (null = not editing). Drives whether AddUserModal opens in edit
  // mode and which submit path runs in handleSubmitUser.
  const [editingUser, setEditingUser] = useState(null);

  const handleSubmitUser = async (userData, files = {}, meta = {}) => {
    const { isEdit = false, userId: editUserId } = meta;
    try {
      setLoading(true);
      const payload = {
        title: userData.title || null,
        firstName: userData.firstName,
        lastName: userData.lastName,
        // Blank means no email, like every other optional field here. Sending "" made the
        // server treat it as a value and collide with any other user holding "".
        email: userData.email || null,
        phone: userData.phone,
        role: userData.role,
        specialization: userData.specialization || null,
        specialityId: userData.specialityId || null,
        practiceLocation: userData.practiceLocation || 'Perveen Aslam Memorial Hospital',
        // Personal data — universal across roles (optional for non-credentialed).
        dateOfBirth: userData.dateOfBirth || null,
        cnic: userData.cnic || null,
        languagesSpoken: userData.languagesSpoken || null,
        // Employment metadata — universal. Status defaults to ACTIVE; designation required.
        qualification: userData.qualification || null,
        qualificationDateCompleted: userData.qualificationDateCompleted || null,
        qualificationDescription: userData.qualificationDescription || null,
        designation: userData.designation || null,
        status: userData.status || 'ACTIVE',
        // One set of hours per person, read by attendance and printed for patients. "" clears
        // them, which puts the person back on the hospital-wide hours.
        workingHours: serializeTimings(userData.workingHours || {}) || '',
        // Contract Agreement — salary is captured for every role; backend stores it on
        // user.salary regardless of role so payroll has one place to read from.
        salary: userData.salary != null && userData.salary !== '' ? Number(userData.salary) : null,
      };
      // Doctor/Nurse/Pharmacist also send the certificate block. Doctor adds the
      // practitioner/practice/speciality + services overrides.
      if (['DOCTOR', 'NURSE', 'PHARMACIST'].includes(userData.role)) {
        payload.pmdcNumber = userData.pmdcNumber || null;
        payload.pmdcIssueDate = userData.pmdcIssueDate || null;
        payload.pmdcExpiryDate = userData.pmdcExpiryDate || null;
      }
      if (userData.role === 'DOCTOR') {
        payload.practitionerType = userData.practitionerType || null;
        payload.practiceType = userData.practiceType || null;
        // Services-provided on create only — they're managed separately under Settings
        // → Medical Services after the user exists.
        if (!isEdit) payload.servicesProvided = userData.servicesProvided || [];
      }

      // Route to update endpoint on edit, create endpoint otherwise.
      const response = isEdit
        ? { user: await userAPI.update(editUserId, payload) }
        : await userAPI.create(payload);

      // File uploads work the same way for create + edit — uploads replace any existing
      // file on the user record. Best-effort: a failed upload logs a toast but doesn't
      // roll back the user save.
      const userId = isEdit ? editUserId : response?.user?.id;
      if (userId) {
        const isCredentialedRole = ['DOCTOR', 'NURSE', 'PHARMACIST'].includes(userData.role);
        const verb = isEdit ? 'updated' : 'created';
        if (isCredentialedRole && files.pmdcFile) {
          const certLabel = userData.role === 'DOCTOR' ? 'PMDC certificate' : 'certificate';
          try { await userAPI.uploadPmdcCertificate(userId, files.pmdcFile); }
          catch (e) { showToast('warning', `User ${verb}, but ${certLabel} upload failed: ` + e.message); }
        }
        if (files.resumeFile) {
          try { await userAPI.uploadResume(userId, files.resumeFile); }
          catch (e) { showToast('warning', `User ${verb}, but resume upload failed: ` + e.message); }
        }
        if (files.qualificationFile) {
          try { await userAPI.uploadQualificationFile(userId, files.qualificationFile); }
          catch (e) { showToast('warning', `User ${verb}, but qualification file upload failed: ` + e.message); }
        }
        if (files.profileImageFile) {
          try { await userAPI.uploadProfileImage(userId, files.profileImageFile); }
          catch (e) { showToast('warning', `User ${verb}, but profile picture upload failed: ` + e.message); }
        } else if (files.removeProfileImage && isEdit) {
          // Existing photo cleared without a replacement → delete it.
          try { await userAPI.deleteProfileImage(userId); }
          catch (e) { showToast('warning', `User ${verb}, but removing the profile picture failed: ` + e.message); }
        }
        if (files.contractFile) {
          try { await userAPI.uploadContractAgreement(userId, files.contractFile); }
          catch (e) { showToast('warning', `User ${verb}, but contract agreement upload failed: ` + e.message); }
        }
        if (files.certificationsFile) {
          try { await userAPI.uploadCertifications(userId, files.certificationsFile); }
          catch (e) { showToast('warning', `User ${verb}, but certifications upload failed: ` + e.message); }
        }
        if (files.cnicFile) {
          try { await userAPI.uploadCnicDocument(userId, files.cnicFile); }
          catch (e) { showToast('warning', `User ${verb}, but CNIC front upload failed: ` + e.message); }
        }
        if (files.cnicBackFile) {
          try { await userAPI.uploadCnicBackDocument(userId, files.cnicBackFile); }
          catch (e) { showToast('warning', `User ${verb}, but CNIC back upload failed: ` + e.message); }
        }
      }

      if (isEdit) {
        setEditingUser(null);
        showToast('success', 'User updated successfully');
        loadUsers();
      } else {
        setShowAddModal(false);
        setNewUserCredentials({ ...response.user, tempPassword: response.tempPassword });
        setShowSuccessModal(true);
        loadUsers();
      }
    } catch (err) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} user:`, err);
      showToast('error', `Failed to ${isEdit ? 'update' : 'create'} user: ` + err.message);
    } finally {
      setLoading(false);
    }
  };
  // Kept as an alias for backward compat — older inline callers still reference this
  // name (e.g. <AddUserModal onSubmit={handleCreateUser} />).
  const handleCreateUser = handleSubmitUser;

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleResetPassword = async (userId) => {
    const ok = await confirm({
      title: 'Reset Password',
      subtitle: 'A new temporary password will be generated',
      message: 'Are you sure you want to reset this user\'s password? The old password will stop working immediately.',
      confirmText: 'Reset Password',
      tone: 'warning',
    });
    if (!ok) return;
    try {
      const response = await userAPI.resetPassword(userId);
      const user = users.find(u => u.id === userId) || selectedUser;
      setNewUserCredentials({
        fullName: user?.fullName || 'User',
        username: response.username,
        tempPassword: response.tempPassword,
        role: user?.role
      });
      setShowViewModal(false);
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error resetting password:', err);
      showToast('error', 'Failed to reset password: ' + err.message);
    }
  };

  // Status-changing actions (deactivate/activate/delete/restore) all flow through the
  // same StatusChangeModal — the parent opens the dialog with the right action, and
  // submitStatusChange routes to the matching API call. Centralising it here keeps the
  // reason+description+today-date contract consistent across all four actions.
  const openStatusChange = (userId, action) => {
    const user = users.find(u => u.id === userId) || selectedUser;
    if (!user) return;
    setStatusChange({ user, action });
  };

  // Backwards-compatible adapter — ViewUserModal still calls onUpdateStatus(newStatus).
  // Map the target status back to the activate/deactivate action keyword.
  const handleUpdateStatus = (userId, newStatus) => {
    openStatusChange(userId, newStatus === 'ACTIVE' ? 'activate' : 'deactivate');
  };

  const handleDeleteUser = (userId) => openStatusChange(userId, 'delete');
  const handleRestoreUser = (userId) => openStatusChange(userId, 'restore');

  const submitStatusChange = async ({ reason, description }) => {
    if (!statusChange) return;
    const { user, action } = statusChange;
    try {
      if (action === 'delete') {
        await userAPI.delete(user.id, reason, description);
      } else if (action === 'restore') {
        await userAPI.restore(user.id, reason, description);
      } else {
        const targetStatus = action === 'activate' ? 'ACTIVE' : 'INACTIVE';
        await userAPI.updateStatus(user.id, targetStatus, reason, description);
      }
      setStatusChange(null);
      setShowViewModal(false);
      loadUsers();
      const verbPast = {
        activate: 'activated',
        deactivate: 'deactivated',
        delete: 'deleted',
        restore: 'restored',
      }[action];
      showToast('success', `${user.fullName} ${verbPast}`);
    } catch (err) {
      console.error(`Error performing ${action}:`, err);
      showToast('error', `Failed to ${action}: ` + err.message);
    }
  };

  const getRoleStats = () => {
    const stats = {};
    Object.keys(ROLE_CONFIG).forEach(role => {
      stats[role] = users.filter(u => u.role === role).length;
    });
    return stats;
  };

  const roleStats = getRoleStats();

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="h-3.5 w-3.5 text-gray-300" />;
    return sortDir === 'asc'
      ? <ChevronUp className="h-3.5 w-3.5 text-emerald-600" />
      : <ChevronDown className="h-3.5 w-3.5 text-emerald-600" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                <Users className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
                <p className="text-gray-600">Manage staff accounts and permissions</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center space-x-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
            >
              <UserPlus className="h-5 w-5" />
              <span>Add New User</span>
            </button>
          </div>
        </div>

        {/* Role Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {Object.entries(ROLE_CONFIG).map(([role, config]) => {
            const Icon = config.icon;
            const count = roleStats[role] || 0;
            const isActive = roleFilter === role;
            return (
              <button
                key={role}
                onClick={() => { setRoleFilter(isActive ? 'ALL' : role); }}
                className={`bg-white rounded-xl shadow-md p-4 transition-all hover:shadow-lg ${isActive ? `ring-2 ${config.borderColor}` : ''}`}
              >
                <div className={`w-10 h-10 bg-gradient-to-br ${config.color} rounded-lg flex items-center justify-center mb-2`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{count}</div>
                <div className="text-xs text-gray-500">{config.label}s</div>
              </button>
            );
          })}
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-xl p-5 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, username, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Select
                value={roleFilter}
                onChange={setRoleFilter}
                options={[{ value: 'ALL', label: 'All Roles' }, ...Object.entries(ROLE_CONFIG).map(([role, config]) => ({ value: role, label: config.label }))]}
                className="min-w-[150px]"
                buttonClassName="py-3 rounded-xl"
              />
            </div>
            <div className="relative">
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={[{ value: 'ALL', label: 'All Status' }, ...Object.entries(STATUS_CONFIG).map(([status, config]) => ({ value: status, label: config.label }))]}
                className="min-w-[130px]"
                buttonClassName="py-3 rounded-xl"
              />
            </div>
            {(roleFilter !== 'ALL' || statusFilter !== 'ALL') && (
              <button onClick={() => { setRoleFilter('ALL'); setStatusFilter('ALL'); setSearchTerm(''); }} className="px-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap">
                Clear filters
              </button>
            )}
            <button title="Refresh" onClick={loadUsers} disabled={loading} className="p-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
              <RefreshCw className={`h-5 w-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg flex items-center justify-between">
            <div className="flex items-center"><AlertCircle className="h-5 w-5 text-red-500 mr-2" /><span className="text-red-700">{error}</span></div>
            <button onClick={loadUsers} className="text-red-600 hover:text-red-800 font-medium text-sm">Retry</button>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-2xl shadow-xl">
          {!loading && <TablePager paging={paging} noun="user" filteredFrom={users.length} />}
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {/* Role + Status moved into the User cell as pills under the name, so the
                    table is narrower and the most-relevant info sits beside the avatar.
                    Role/Status sorting is preserved via the User column toggling fullName. */}
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" aria-sort={sortField === 'fullName' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" onClick={() => toggleSort('fullName')} className="flex items-center space-x-1 w-full uppercase tracking-wider rounded hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"><span>User</span><SortIcon field="fullName" /></button>
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Contact</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Speciality</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell" aria-sort={sortField === 'createdAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" onClick={() => toggleSort('createdAt')} className="flex items-center space-x-1 w-full uppercase tracking-wider rounded hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"><span>Joined</span><SortIcon field="createdAt" /></button>
                </th>
                <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                        <div>
                          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mb-1"></div>
                          <div className="h-3 w-20 bg-gray-200 rounded animate-pulse mb-1.5"></div>
                          <div className="flex gap-1.5">
                            <div className="h-5 w-16 bg-gray-200 rounded-full animate-pulse"></div>
                            <div className="h-5 w-14 bg-gray-200 rounded-full animate-pulse"></div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell"><div className="h-3 w-36 bg-gray-200 rounded animate-pulse"></div></td>
                    <td className="px-6 py-4 hidden md:table-cell"><div className="h-3 w-24 bg-gray-200 rounded animate-pulse"></div></td>
                    <td className="px-6 py-4 hidden lg:table-cell"><div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div></td>
                    <td className="px-6 py-4"></td>
                  </tr>
                ))
              ) : sortedUsers.length > 0 ? (
                paging.pageRows.map((user) => {
                  const roleConfig = ROLE_CONFIG[user.role];
                  const statusConfig = STATUS_CONFIG[user.status];
                  const Icon = roleConfig?.icon || UserIcon;

                  return (
                    // Row click opens the View modal — same handler the old "View Details"
                    // menu item used. The action buttons in the rightmost cell stop
                    // propagation so they don't trigger this on click.
                    <tr
                      key={user.id}
                      tabIndex={0}
                      onClick={() => handleViewUser(user)}
                      onKeyDown={onActivateKey(() => handleViewUser(user))}
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${ROW_FOCUS_RING}`}
                    >
                      {/* User — name + username + Role/Status pills stacked under it.
                          Pills sit on the same row, wrap on narrow widths. */}
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          {user.profileImageUrl ? (
                            <AvatarHoverPreview imageUrl={user.profileImageUrl} name={user.fullName} size="w-10 h-10" rounded="rounded-lg" />
                          ) : (
                            <div className={`w-10 h-10 bg-gradient-to-br ${roleConfig?.color || 'from-gray-400 to-gray-500'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                              <span className="text-white font-bold text-sm">
                                {user.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
                              </span>
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900 truncate">{user.fullName}</div>
                            <div className="text-sm text-gray-500 font-mono">@{user.username}</div>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${roleConfig?.badgeBg || 'bg-gray-100 text-gray-600'}`}>
                                <Icon className="h-3 w-3" />
                                {roleConfig?.label || user.role}
                              </span>
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig?.color || 'bg-gray-100'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${statusConfig?.dot || 'bg-gray-400'}`}></span>
                                {statusConfig?.label || user.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      {/* Contact — email on top, phone below. Em-dash only when both are
                          missing, so an empty email doesn't leave a stray "—" above a
                          present phone number. */}
                      <td className="px-6 py-4 hidden lg:table-cell">
                        {user.email && (
                          <div className="text-sm text-gray-600 truncate max-w-[200px]">{user.email}</div>
                        )}
                        {user.phone && (
                          <div className="text-xs text-gray-400">{user.phone}</div>
                        )}
                        {!user.email && !user.phone && (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      {/* Specialization */}
                      <td className="px-6 py-4 hidden md:table-cell">
                        <span className="text-sm text-gray-600">{user.specialization || '—'}</span>
                      </td>
                      {/* Joined */}
                      <td className="px-6 py-4 hidden lg:table-cell">
                        <span className="text-sm text-gray-500">
                          {formatClinicDate(user.createdAt)}
                        </span>
                      </td>
                      {/* Actions — Inline icon buttons (no dropdown). Each tinted with
                          its semantic color: violet for permissions, amber for password
                          reset, red/emerald for the deactivate↔activate flip, red for
                          delete. Deleted users only get a Restore button — none of the
                          other actions are meaningful on a soft-deleted record. */}
                      <td className="px-6 py-4 text-right">
                        {user.status === 'DELETED' ? (
                          can('DELETE') ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRestoreUser(user.id); }}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium border border-emerald-200 transition-colors"
                              title="Restore User"
                            >
                              <RefreshCw className="h-4 w-4" />
                              Restore
                            </button>
                          ) : null
                        ) : (
                          <div className="inline-flex items-center gap-1">
                            {can('MANAGE_PERMISSIONS') && user.id !== myId && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setPermEditUser(user); }}
                                className="p-2 bg-violet-50 hover:bg-violet-100 text-violet-600 hover:text-violet-700 rounded-lg transition-colors border border-violet-100"
                                title="Edit Permissions"
                              >
                                <Shield className="h-4 w-4" />
                              </button>
                            )}
                            {can('RESET_PASSWORD') && user.id !== myId && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedUser(user); handleResetPassword(user.id); }}
                                className="p-2 bg-amber-50 hover:bg-amber-100 text-amber-600 hover:text-amber-700 rounded-lg transition-colors border border-amber-100"
                                title="Reset Password"
                              >
                                <Key className="h-4 w-4" />
                              </button>
                            )}
                            {can('SUSPEND_USER') && user.id !== myId && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleUpdateStatus(user.id, user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'); }}
                                className={`p-2 rounded-lg transition-colors border ${
                                  user.status === 'ACTIVE'
                                    ? 'bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 border-red-100'
                                    : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-600 hover:text-emerald-700 border-emerald-100'
                                }`}
                                title={user.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                              >
                                <Power className="h-4 w-4" />
                              </button>
                            )}
                            {can('DELETE') && user.id !== myId && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteUser(user.id); }}
                                className="p-2 bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 rounded-lg transition-colors border border-red-100"
                                title="Delete User"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-16 text-center">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-500 mb-1">No Users Found</h3>
                    <p className="text-gray-400 text-sm">
                      {searchTerm || roleFilter !== 'ALL' || statusFilter !== 'ALL'
                        ? 'Try adjusting your search or filters'
                        : 'Start by adding your first user'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {!loading && <TablePager paging={paging} noun="user" filteredFrom={users.length} position="bottom" />}
        </div>

        {/* Add User Modal */}
        {showAddModal && (
          <AddUserModal
            onClose={() => setShowAddModal(false)}
            onSubmit={handleSubmitUser}
            roleConfig={ROLE_CONFIG}
            existingRoles={roles}
            onRolesChanged={async () => {
              try {
                const data = await rolePermissionAPI.getRoles();
                setRoles(Array.isArray(data) ? data : []);
              } catch { /* leave existing roles in place on transient failure */ }
            }}
          />
        )}

        {/* Edit User Modal — same AddUserModal component, just with existingUser set so
            it skips the role picker and pre-populates everything. */}
        {editingUser && (
          <AddUserModal
            existingUser={editingUser}
            onClose={() => setEditingUser(null)}
            onSubmit={handleSubmitUser}
            roleConfig={ROLE_CONFIG}
            existingRoles={roles}
          />
        )}

        {/* View User Modal */}
        {showViewModal && selectedUser && (
          <ViewUserModal
            user={selectedUser}
            roleConfig={ROLE_CONFIG}
            onClose={() => { setShowViewModal(false); setSelectedUser(null); }}
            onResetPassword={() => handleResetPassword(selectedUser.id)}
            onUpdateStatus={(status) => handleUpdateStatus(selectedUser.id, status)}
            onDelete={can('DELETE') && selectedUser.id !== myId ? () => handleDeleteUser(selectedUser.id) : null}
            onRestore={can('DELETE') ? () => handleRestoreUser(selectedUser.id) : null}
            onEdit={can('EDIT') ? () => { setEditingUser(selectedUser); setShowViewModal(false); } : null}
          />
        )}

        {/* Status Change Modal — gathers reason + description for any status-changing
            action (deactivate / activate / delete / restore). Date is today, read-only.
            Reason dropdown options vary per action so audit entries stay consistent. */}
        {statusChange && (
          <StatusChangeModal
            user={statusChange.user}
            action={statusChange.action}
            onClose={() => setStatusChange(null)}
            onSubmit={submitStatusChange}
          />
        )}

        {/* Success Modal with Credentials */}
        {showSuccessModal && newUserCredentials && (
          <CredentialsModal
            user={newUserCredentials}
            roleConfig={ROLE_CONFIG}
            onClose={() => { setShowSuccessModal(false); setNewUserCredentials(null); }}
          />
        )}

        {/* Edit User Permissions Modal */}
        {permEditUser && (
          <UserPermissionsModal
            user={permEditUser}
            onClose={() => setPermEditUser(null)}
            onSaved={() => { setPermEditUser(null); showToast('success', 'User permissions updated'); }}
          />
        )}
      </div>
    </div>
  );
};

// ============ ADD / EDIT USER MODAL ============
// When `existingUser` is passed, the modal renders in edit mode: role picker is
// skipped, formData is pre-populated from the user record, and Save calls the parent's
// onSubmit with (payload, files, isEdit=true) so the parent routes to PUT /api/users/{id}
// instead of the create flow. Role itself isn't editable post-create — too many cascading
// changes (permissions, role-specific fields).
const AddUserModal = ({ onClose, onSubmit, roleConfig: ROLE_CONFIG = {}, existingRoles = [], onRolesChanged, existingUser = null }) => {
  const isEdit = !!existingUser;
  const showToast = useToast();
  // "+ Add new role" opens the same CreateRoleModal used by Roles & Permissions, so admins
  // can spin up a custom role (e.g. Lab Technician, Cleaner) without leaving the Create
  // User flow. On success the parent re-fetches roles and we auto-select the new role.
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [formData, setFormData] = useState(() => {
    // Edit mode — hydrate from the existing user record. Falsy/missing fields become
    // empty strings so the form inputs are controlled. Languages are split back into
    // an array from the comma-separated wire format.
    if (existingUser) {
      return {
        title: existingUser.title || '',
        workingHours: parseTimings(existingUser.workingHours),
        firstName: existingUser.firstName || '',
        lastName: existingUser.lastName || '',
        email: existingUser.email || '',
        phone: existingUser.phone || '',
        role: existingUser.role || '',
        specialization: existingUser.specialization || '',
        specialityId: existingUser.specialityId || '',
        practiceLocation: existingUser.practiceLocation || 'Perveen Aslam Memorial Hospital',
        dateOfBirth: existingUser.dateOfBirth || '',
        cnic: existingUser.cnic || '',
        languages: existingUser.languagesSpoken
          ? existingUser.languagesSpoken.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        pmdcNumber: existingUser.pmdcNumber || '',
        pmdcIssueDate: existingUser.pmdcIssueDate || '',
        pmdcExpiryDate: existingUser.pmdcExpiryDate || '',
        practitionerType: existingUser.practitionerType || '',
        practiceType: existingUser.practiceType || '',
        // Services Provided are managed separately on Doctor (each is its own MedicalService
        // record); we don't re-create them on edit — admin uses Settings → Medical Services.
        servicesProvided: [],
        qualification: existingUser.qualification || '',
        designation: existingUser.designation || '',
        accountStatus: existingUser.status || 'ACTIVE',
        // Whole rupees: the field groups bare digits, so a stored 36300.00 must not arrive with a
        // decimal part for the grouping to swallow.
        salary: existingUser.salary != null ? String(Math.round(Number(existingUser.salary) || 0)) : '',
        qualificationDateCompleted: existingUser.qualificationDateCompleted || '',
        qualificationDescription: existingUser.qualificationDescription || '',
      };
    }
    return {
      title: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: '',
      specialization: '',
      specialityId: '',
      practiceLocation: 'Perveen Aslam Memorial Hospital',
      // Doctor-only fields (ignored on submit when role !== 'DOCTOR')
      dateOfBirth: '',
      cnic: '',
      languages: [], // multi-select; serialized to comma-separated on submit
      pmdcNumber: '',
      pmdcIssueDate: '',
      pmdcExpiryDate: '',
      practitionerType: '',
      practiceType: '',
      // Per-doctor service+fee assignments. Each entry: { serviceId, name, fee }. On submit
      // we send [{ serviceId, fee }] and the backend creates a doctor-specific MedicalService
      // copy per entry (with baseFee=fee, doctor=newUser) — same pattern as the existing
      // doctor-specific pricing in Settings → Medical Services.
      servicesProvided: [],
      // Employment metadata captured for every role (Designation required server-side,
      // qualification + qualification file optional).
      qualification: '',
      designation: '',
      // Empty means they follow the hospital-wide hours, which is how everyone starts.
      workingHours: {},
      accountStatus: 'ACTIVE',
      // Doctor Contract Agreement — monthly salary in PKR. Commission is captured per-service
      // inside servicesProvided[].commission so payouts can be computed per visit later.
      salary: '',
      // Educational document metadata — captured for custom (non-system) roles alongside
      // the qualification file upload. Both optional; backend columns are nullable.
      qualificationDateCompleted: '',
      qualificationDescription: '',
    };
  });
  // Files held in component state until Create User succeeds, then uploaded to the new user
  // via dedicated endpoints. The certificate is required for Doctor/Nurse/Pharmacist and the
  // resume for the doctor alone; everything else is there to be uploaded if it exists.
  const [pmdcFile, setPmdcFile] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  // True when an existing profile photo was removed (Edit mode, no replacement staged).
  const [removeProfileImage, setRemoveProfileImage] = useState(false);
  const [qualificationFile, setQualificationFile] = useState(null);
  // Doctor Contract Agreement copy — signed agreement between Hospital and Doctor.
  const [contractFile, setContractFile] = useState(null);
  // Technician certifications document — vendor / training certs combined into one file.
  const [certificationsFile, setCertificationsFile] = useState(null);
  // CNIC document — front + back, staged then uploaded after user create/update.
  const [cnicFile, setCnicFile] = useState(null); // front
  const [cnicBackFile, setCnicBackFile] = useState(null); // back

  // 2-step wizard for Add: pick the role first, then fill the role-specific details.
  // Edit skips role picking — role is locked to whatever's on the existing user record.
  const [step, setStep] = useState(isEdit ? 'details' : 'role'); // 'role' | 'details'

  // Accordion state for step 2 — only one section open at a time. Personal starts open
  // since it's the natural first stop after picking a role. Click an open section to
  // collapse it (null), click a closed one to open it (and collapse whichever was open).
  const [openSection, setOpenSection] = useState('personal');
  // Scroll container (the modal body), used to scroll the first invalid field into view
  // when a save is blocked by validation errors in a collapsed section, and reset to the
  // top whenever the wizard step changes (see effect below).
  const modalScrollRef = useRef(null);
  // Every time the user lands on step 2 (initial Next click or returning from step 1),
  // snap back to Personal so the user always starts on the natural first section instead
  // of whatever they had toggled open the last time around. Also reset the modal body's
  // own scroll position so the new step's content isn't stuck at the previous step's
  // scroll offset (the modal body is a separate scroller from the app shell).
  useEffect(() => {
    if (step === 'details') setOpenSection('personal');
    modalScrollRef.current?.scrollTo({ top: 0 });
  }, [step]);
  const sectionProps = (name) => ({
    open: openSection === name,
    onToggle: () => setOpenSection(s => (s === name ? null : name)),
  });
  // Accordion sections in display order. Used on a failed save to open the top-most section
  // that has an error so the user isn't left staring at a form that "did nothing".
  const SECTION_ORDER = ['personal', 'working-hours', 'employment', 'professional-details', 'contract'];
  // Maps a validation error key to the accordion section that renders it. designation +
  // resumeFile live in Employment for the non-credentialed system roles (Admin / Accounts /
  // Receptionist) and in Professional Details for everyone else.
  const sectionForField = (field) => {
    if (['salary'].includes(field)) return 'contract';
    if (['specialization', 'practiceType', 'practitionerType', 'pmdcNumber', 'pmdcIssueDate',
      'pmdcExpiryDate', 'pmdcFile'].includes(field)) {
      return 'professional-details';
    }
    if (['designation', 'resumeFile'].includes(field)) {
      return ['ADMIN', 'ACCOUNTS', 'RECEPTIONIST'].includes(formData.role)
        ? 'employment'
        : 'professional-details';
    }
    // firstName, lastName, email, phone, cnic, dateOfBirth, role
    return 'personal';
  };

  // Doctor/Nurse/Pharmacist/Dispenser all capture the same profile block (DOB, languages,
  // resume). Doctor additionally needs practitioner/practice/speciality/services.
  const isCredentialedRole = ['DOCTOR', 'NURSE', 'PHARMACIST', 'DISPENSER'].includes(formData.role);
  // A dispenser works the pharmacy counter with no licence to record, so the certificate block is
  // not shown for them at all. Technician still sees it, optional, for their own certifications.
  const showsCertificate = ['NURSE', 'PHARMACIST', 'TECHNICIAN'].includes(formData.role);
  const certificateRequired = ['DOCTOR', 'NURSE', 'PHARMACIST'].includes(formData.role);
  // For Doctor we say "PMDC" (Pakistan Medical & Dental Council); for Nurse/Pharmacist we
  // use a generic "Certificate" label. Same backend columns (pmdc_*) — labels only.
  const certLabel = formData.role === 'DOCTOR' ? 'PMDC' : 'Certificate';
  // Custom (admin-created) role — anything that isn't one of the 7 system roles. These
  // get their own merged "Professional Details" section since the system-role-specific
  // accordions (Doctor's licensing, Nurse/Pharmacist's certificate, etc.) don't apply.
  const SYSTEM_ROLES = ['ADMIN', 'ACCOUNTS', 'DOCTOR', 'NURSE', 'PHARMACIST', 'RECEPTIONIST', 'TECHNICIAN', 'DISPENSER'];

  // CNIC formatting, with the caret kept in place while a digit mid-string is corrected, is shared
  // with the patient form: ../utils/cnic.
  const isValidCNIC = (value) => /^\d{5}-\d{7}-\d$/.test(String(value || '').trim());
  // Inline blur validator — the CNIC is required of every role, and must match the
  // format. Mirrors the validateForm logic so the user sees the same error message
  // inline as on submit.
  const validateCNICField = (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return 'CNIC is required';
    if (trimmed && !isValidCNIC(trimmed)) return 'CNIC must be in the format 42401-1234567-8';
    return null;
  };
  const isCustomRole = formData.role && !SYSTEM_ROLES.includes(formData.role);
  // Speciality options come from the managed catalog (Settings → Specialities). A legacy free-text
  // value that isn't in the catalog is kept selectable so editing an old user doesn't blank it.
  const [specialities, setSpecialities] = useState([]);
  useEffect(() => {
    specialityAPI.list()
      .then(d => setSpecialities(Array.isArray(d) ? d : []))
      .catch(() => setSpecialities([]));
  }, []);
  const specialityOptions = React.useMemo(() => {
    const names = specialities.map(s => s.name);
    const current = (formData.specialization || '').trim();
    if (current && !names.some(n => n.toLowerCase() === current.toLowerCase())) names.push(current);
    return names.sort((a, b) => a.localeCompare(b));
  }, [specialities, formData.specialization]);
  const [medicalServices, setMedicalServices] = useState([]);
  const [servicePickerId, setServicePickerId] = useState(''); // currently-highlighted in dropdown
  useEffect(() => {
    // Only general (catalog) services — doctor-specific copies are hidden from the picker
    // so the same name doesn't appear once per doctor that already has an override.
    medicalServiceAPI.getAll({ generalOnly: true })
      .then(d => setMedicalServices(Array.isArray(d) ? d : []))
      .catch(() => setMedicalServices([]));
  }, []);
  const [errors, setErrors] = useState({});
  const [phoneTouched, setPhoneTouched] = useState(false);
  const phoneCountry = usePhoneCountryCode(formData.phone);
  const [previewUsername, setPreviewUsername] = useState('');

  useEffect(() => {
    if (formData.firstName && formData.lastName) {
      const base = (formData.firstName.charAt(0) + formData.lastName).toLowerCase().replace(/[^a-z]/g, '');
      setPreviewUsername(base);
    } else {
      setPreviewUsername('');
    }
  }, [formData.firstName, formData.lastName]);

  const addUserDialogRef = useDialog(onClose);

  const validateForm = () => {
    const errs = {};
    if (!formData.firstName.trim()) errs.firstName = 'First name is required';
    if (!formData.lastName.trim()) errs.lastName = 'Last name is required';
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Invalid email format';
    }
    if (!formData.phone.trim() || !validatePhone(formData.phone)) errs.phone = 'Enter a valid phone number';
    if (!formData.role) errs.role = 'Role is required';
    // CNIC is required of every member of staff, whatever the role.
    if (!formData.cnic.trim()) {
      errs.cnic = 'CNIC is required';
    } else if (!/^\d{5}-\d{7}-\d$/.test(formData.cnic.trim())) {
      errs.cnic = 'CNIC must be in the format 42401-1234567-8';
    }
    // Credentialed-role block — Doctor, Nurse, Pharmacist, Dispenser (same fields).
    if (isCredentialedRole) {
      if (!formData.dateOfBirth) errs.dateOfBirth = 'Date of birth is required';
    }
    // Only a doctor has to produce a resume; for everyone else it is there to be uploaded if they
    // have one. File requirements apply on Create only: on Edit the file already on record counts
    // as fulfilled (the admin can replace it but isn't forced to re-upload).
    if (formData.role === 'DOCTOR' && !isEdit && !resumeFile) {
      errs.resumeFile = 'Resume file is required';
    }
    // The certificate half is skipped for a dispenser, who has none to produce.
    if (certificateRequired) {
      if (!formData.pmdcNumber.trim()) errs.pmdcNumber = `${certLabel} number is required`;
      if (!formData.pmdcIssueDate) errs.pmdcIssueDate = `${certLabel} issue date is required`;
      if (!formData.pmdcExpiryDate) errs.pmdcExpiryDate = `${certLabel} expiry date is required`;
      if (formData.pmdcIssueDate && formData.pmdcExpiryDate
          && formData.pmdcExpiryDate <= formData.pmdcIssueDate) {
        errs.pmdcExpiryDate = 'Expiry date must be after the issue date';
      }
      if (!isEdit && !pmdcFile) errs.pmdcFile = `${certLabel} file is required`;
    }
    // Doctor-only extras on top of the credentialed block.
    if (formData.role === 'DOCTOR') {
      if (!formData.practiceType) errs.practiceType = 'Practice type is required';
      if (!formData.practitionerType) errs.practitionerType = 'Practitioner type is required';
      if (!formData.specialization.trim()) errs.specialization = 'Specialization is required for doctors';
    }
    // Contract Agreement — captured for every role. Salary is required; the signed
    // contract file is optional (often signed in person and uploaded later). Doctor
    // additionally captures per-service commission inside servicesProvided, which is
    // optional on each row.
    if (formData.role) {
      if (formData.salary === '' || formData.salary == null) {
        errs.salary = 'Monthly salary is required';
      } else if (Number(formData.salary) < 0) {
        errs.salary = 'Salary cannot be negative';
      }
    }
    // Employment metadata — designation required for any role.
    if (formData.role && !formData.designation.trim()) {
      errs.designation = 'Designation is required';
    }
    setErrors(errs);
    return errs;
  };

  const handleSubmit = () => {
    const errs = validateForm();
    if (Object.keys(errs).length > 0) {
      // Don't leave the user guessing: some required fields may live in a collapsed
      // accordion section. Tell them via a toast, open the top-most section that has an
      // error, and scroll the first invalid field into view.
      const firstSection = SECTION_ORDER.find(sec =>
        Object.keys(errs).some(field => sectionForField(field) === sec),
      );
      if (firstSection) setOpenSection(firstSection);
      const count = Object.keys(errs).length;
      showToast('error', `Please fill in ${count} required field${count !== 1 ? 's' : ''} before saving.`);
      // Wait for the section to expand (it mounts on the next render), then scroll the
      // first error message into view within the modal body.
      setTimeout(() => {
        const firstError = modalScrollRef.current?.querySelector('.text-red-500');
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 60);
      return;
    }
    // Serialize role-conditional fields into the JSON payload. Files are uploaded after
    // the user is created (see UserManagement.handleCreateUser).
    const payload = { ...formData };
    // Languages can apply to any role now since it lives in Personal (optional for
    // non-credentialed). Send the comma-joined string the backend expects.
    payload.languagesSpoken = (formData.languages || []).join(',');
    // Employment fields — server expects 'status' (not 'accountStatus').
    payload.status = formData.accountStatus;
    if (formData.role === 'DOCTOR') {
      // Strip the UI-only 'name' field — backend only needs (serviceId, fee, commission).
      payload.servicesProvided = (formData.servicesProvided || []).map(s => ({
        serviceId: s.serviceId,
        fee: Number(s.fee) || 0,
        commission: s.commission === '' || s.commission == null ? null : Number(s.commission),
      }));
    }
    delete payload.languages; // server expects languagesSpoken
    delete payload.accountStatus; // server expects status
    // On edit, drop fields that the update endpoint doesn't accept (servicesProvided
    // — managed separately under Settings → Medical Services).
    if (isEdit) {
      delete payload.servicesProvided;
    }
    onSubmit(
      payload,
      { pmdcFile, resumeFile, profileImageFile, qualificationFile, contractFile, certificationsFile, cnicFile, cnicBackFile, removeProfileImage },
      { isEdit, userId: existingUser?.id },
    );
  };

  // buildPhone, parsePhone, validatePhone imported from ../utils/phone

  return (
    <div ref={addUserDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={modalScrollRef} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-br from-navy-700 to-navy-900 px-6 py-5 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
                <UserPlus className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{isEdit ? 'Edit User' : 'Add New User'}</h2>
                <p className="text-white/80 text-sm">
                  {isEdit
                    ? `${ROLE_CONFIG[formData.role]?.label || formData.role} · update profile`
                    : step === 'role'
                      ? 'Step 1 of 2 - Select role'
                      : `Step 2 of 2 - ${ROLE_CONFIG[formData.role]?.label || formData.role} details`}
                </p>
              </div>
            </div>
            <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors">
              <X className="h-6 w-6 text-white" />
            </button>
          </div>

          {/* Step indicator — two pill segments on Add only. Edit skips the role
              picker entirely so the wizard becomes a single screen. */}
          {!isEdit && (
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => setStep('role')}
                className={`flex-1 h-1.5 rounded-full transition-colors ${step === 'role' ? 'bg-white' : 'bg-white/60 hover:bg-white/80 cursor-pointer'}`}
                title="Select role"
              />
              <button
                type="button"
                disabled={!formData.role}
                onClick={() => formData.role && setStep('details')}
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  step === 'details' ? 'bg-white' : formData.role ? 'bg-white/30 hover:bg-white/50 cursor-pointer' : 'bg-white/20 cursor-not-allowed'
                }`}
                title="Profile details"
              />
            </div>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* STEP 1 — Role picker only. Step 2 (Personal / Employment / Professional /
              Certificate or Contract Agreement) is hidden until a role is chosen and Next
              is clicked. This keeps the modal short and the first decision unambiguous. */}
          {step === 'role' && (
            <div>
              <div className="mb-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-emerald-600" /> Choose a role for this user
                </h3>
                <p className="text-sm text-gray-500 mt-1">The role determines which fields you'll fill in next.</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(ROLE_CONFIG).map(([role, config]) => {
                  const Icon = config.icon;
                  const isSelected = formData.role === role;
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => setFormData({ ...formData, role, title: role === 'DOCTOR' ? 'Dr.' : '' })}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        isSelected
                          ? `${config.borderColor} ${config.bgLight} shadow-md`
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-10 h-10 bg-gradient-to-br ${config.color} rounded-lg flex items-center justify-center mb-2`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div className={`font-semibold ${isSelected ? config.textColor : 'text-gray-900'}`}>
                        {config.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{config.description}</div>
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setShowCreateRoleModal(true)}
                  className="p-4 rounded-xl border-2 border-dashed border-gray-300 hover:border-emerald-400 hover:bg-emerald-50 text-left transition-all flex flex-col items-start justify-center"
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                    <Plus className="h-5 w-5 text-gray-500" />
                  </div>
                  <div className="font-semibold text-gray-700">Add new role</div>
                  <div className="text-xs text-gray-500 mt-0.5">Create a custom role</div>
                </button>
              </div>
              {errors.role && <p className="text-red-500 text-xs mt-2">{errors.role}</p>}
            </div>
          )}

          {showCreateRoleModal && (
            <CreateRoleModal
              existingRoles={existingRoles}
              onClose={() => setShowCreateRoleModal(false)}
              onCreated={async (newRole) => {
                setShowCreateRoleModal(false);
                if (typeof onRolesChanged === 'function') {
                  await onRolesChanged();
                }
                if (newRole?.name) {
                  setFormData(prev => ({ ...prev, role: newRole.name }));
                }
              }}
            />
          )}

          {step === 'details' && (
            <>
          {/* 2) PERSONAL — identity always; DOB + CNIC + Languages appended for any
                 credentialed role (Doctor / Nurse / Pharmacist). Profile picture sits at
                 the top of the section since it's the most "personal" attribute. */}
          <Section title="Personal" icon={UserIcon} {...sectionProps('personal')}>
            {/* Profile picture — optional, shown for everyone. Uploads after user is
                created. Click the circle to pick an image; click X to clear. */}
            <ProfilePicturePicker
              file={profileImageFile}
              existingUrl={removeProfileImage ? null : authedMediaUrl(existingUser?.profileImageUrl)}
              onChange={(f) => { setProfileImageFile(f); if (f) setRemoveProfileImage(false); }}
              onRemoveExisting={() => setRemoveProfileImage(true)}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex gap-3">
                <div className="w-24 flex-shrink-0">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                  <Select
                    value={formData.title}
                    onChange={(v) => setFormData({ ...formData, title: v })}
                    placeholder="--"
                    options={['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.', 'Mst.']}
                    buttonClassName="p-3.5 border-2 rounded-xl"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    onBlur={(e) => setFormData(f => ({ ...f, firstName: toTitleCase(e.target.value) }))}
                    className={`w-full p-3.5 border-2 rounded-xl focus:ring-2 focus:ring-emerald-500 transition-all ${errors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-emerald-500'}`}
                    placeholder="e.g., Ahmad"
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  onBlur={(e) => setFormData(f => ({ ...f, lastName: toTitleCase(e.target.value) }))}
                  className={`w-full p-3.5 border-2 rounded-xl focus:ring-2 focus:ring-emerald-500 transition-all ${errors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-emerald-500'}`}
                  placeholder="e.g., Hassan"
                />
                {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
              </div>
            </div>

            {formData.role && (
              <>
                {/* DOB/CNIC/Languages show for every role. Required asterisk only flips on
                    for credentialed roles where they're load-bearing for the certificate
                    block (Doctor/Nurse/Pharmacist). */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth{isCredentialedRole ? ' *' : ''}</label>
                    {/* Custom DatePicker — same component the patient form uses, so the
                        date format ("May 1, 1998") and the calendar styling match across
                        Add Patient and Add User. max=today blocks future DOBs. */}
                    <DatePicker
                      value={formData.dateOfBirth}
                      onChange={(v) => { setFormData({ ...formData, dateOfBirth: v }); if (errors.dateOfBirth) setErrors(p => ({ ...p, dateOfBirth: null })); }}
                      max={new Date().toISOString().split('T')[0]}
                      error={!!errors.dateOfBirth}
                      placeholder="Select date of birth"
                      size="lg"
                    />
                    {errors.dateOfBirth && <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">CNIC *</label>
                    <input
                      type="text"
                      value={formData.cnic}
                      onChange={(e) => handleCnicInput(e, formData.cnic, (cnic) => {
                        setFormData({ ...formData, cnic });
                        if (errors.cnic) setErrors(p => ({ ...p, cnic: null }));
                      })}
                      onBlur={() => {
                        const err = validateCNICField(formData.cnic);
                        if (err) setErrors(p => ({ ...p, cnic: err }));
                      }}
                      placeholder="42401-1234567-8"
                      maxLength={15}
                      className={`w-full p-3.5 border-2 rounded-xl focus:ring-2 focus:ring-emerald-500 font-mono transition-all ${errors.cnic ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-emerald-500'}`}
                    />
                    {errors.cnic
                      ? <p className="text-red-500 text-xs mt-1">{errors.cnic}</p>
                      : <p className="text-gray-400 text-xs mt-1">Format: 42401-1234567-8</p>}
                  </div>
                </div>
                {/* CNIC document — front + back scan/photo, staged then uploaded after save. */}
                <CnicDocUpload
                  frontFile={cnicFile}
                  backFile={cnicBackFile}
                  onFront={setCnicFile}
                  onBack={setCnicBackFile}
                  existingFrontUrl={existingUser?.cnicDocumentUrl}
                  existingBackUrl={existingUser?.cnicBackDocumentUrl}
                />
              </>
            )}

            {/* Contact (Email + Phone) — universal. Folded into Personal so the modal
                doesn't have a separate one-line section for two fields. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full pl-12 pr-4 p-3.5 border-2 rounded-xl focus:ring-2 focus:ring-emerald-500 transition-all ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-emerald-500'}`}
                    placeholder="user@clinic.pk"
                  />
                </div>
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
                {(() => {
                  const { localDigits } = parsePhone(formData.phone);
                  const countryCode = phoneCountry.code;
                  const phoneInvalid = phoneTouched && formData.phone && !validatePhone(formData.phone);
                  return (
                    <div className={`flex items-stretch border-2 rounded-xl focus-within:ring-2 transition-all ${
                      errors.phone || phoneInvalid
                        ? 'border-red-500 focus-within:ring-red-200 focus-within:border-red-500'
                        : validatePhone(formData.phone)
                          ? 'border-emerald-400 focus-within:ring-emerald-200 focus-within:border-emerald-500'
                          : 'border-gray-200 focus-within:ring-green-200 focus-within:border-green-500'
                    }`}>
                      <CountryCodeSelect
                        value={countryCode}
                        onChange={(newCode) => {
                          setPhoneTouched(true);
                          phoneCountry.pick(newCode);
                          setFormData({ ...formData, phone: buildPhone(newCode, localDigits) });
                        }}
                        className="rounded-l-xl"
                      />
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={localDigits}
                        onChange={(e) => {
                          setPhoneTouched(true);
                          const digits = e.target.value.replace(/\D/g, '');
                          const next = buildPhone(countryCode, digits);
                          phoneCountry.typed(next);
                          setFormData({ ...formData, phone: next });
                          if (errors.phone) setErrors(prev => ({ ...prev, phone: null }));
                        }}
                        className="flex-1 px-4 py-3.5 border-0 focus:outline-none focus:ring-0 bg-transparent rounded-r-xl"
                        placeholder={countryCode === '92' ? '3001234567' : 'Phone number'}
                        maxLength={getPhoneLength(countryCode).max}
                      />
                    </div>
                  );
                })()}
                {(errors.phone || (phoneTouched && formData.phone && !validatePhone(formData.phone))) && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone || 'Enter a valid phone number'}</p>
                )}
              </div>
            </div>

            {/* Languages — Primary pair (English / Urdu) always visible as toggle pills.
                Less-common languages selected from the "+ More" dropdown render as
                removable chips alongside. Optional for every role. */}
            {isCredentialedRole && (
              <LanguagesPicker
                selected={formData.languages}
                onChange={(next) => setFormData({ ...formData, languages: next })}
              />
            )}

            {previewUsername && (
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Auto-generated username</p>
                      <p className="font-bold text-emerald-700 font-mono text-lg">{previewUsername}</p>
                    </div>
                  </div>
                  <BadgeCheck className="h-6 w-6 text-emerald-500" />
                </div>
              </div>
            )}
          </Section>

          {/* WORKING HOURS — every role, its own section because the role-specific sections below
              are mutually exclusive and these are not. One set of hours per person (owner,
              2026-10-05): attendance judges a late arrival and a full day against them, and for a
              doctor the same hours print on the prescription and the token slip and show on the
              Display Board. They used to be set in two other places. */}
          <Section title="Working Hours" icon={CalendarClock} {...sectionProps('working-hours')}>
            <WorkingHoursEditor
              value={formData.workingHours}
              onChange={(byDay) => setFormData({ ...formData, workingHours: byDay })}
              forPatients={formData.role === 'DOCTOR'}
            />
          </Section>

          {/* 4) EMPLOYMENT — universal HR metadata: designation, account status,
                 qualification (+ file), resume. Shown only for system roles WITHOUT a
                 merged Professional Details section — i.e. Admin / Accounts /
                 Receptionist. Doctor / Nurse / Pharmacist / Technician get their own
                 merged section below; custom roles get a dedicated one too. */}
          {['ADMIN', 'ACCOUNTS', 'RECEPTIONIST'].includes(formData.role) && (
            <Section title="Employment" icon={BadgeCheck} {...sectionProps('employment')}>
              {/* Account Status dropped from Add — new users always default to ACTIVE,
                  same as Doctor / Nurse / Pharmacist / Technician. Status changes happen
                  via Deactivate / Suspend / Restore actions on the user row. */}
              <SearchableSelect
                label="Designation *"
                value={formData.designation}
                onChange={(val) => { setFormData({ ...formData, designation: val }); if (errors.designation) setErrors(p => ({ ...p, designation: null })); }}
                options={designationsFor(formData.role)}
                placeholder="e.g. Receptionist, Officer, Manager"
                error={errors.designation}
                icon={<BadgeCheck className="h-4 w-4 text-gray-400" />}
                allowCustom
              />
              {/* Qualification text + optional degree scan. Doctors don't get the file
                  picker — the PMDC certificate upload (Professional section) already
                  serves as proof of qualification, so a separate degree upload is
                  redundant. The text field stays for everyone. */}
              <div className={`grid gap-4 ${formData.role === 'DOCTOR' ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Qualification</label>
                  <input
                    type="text"
                    value={formData.qualification}
                    onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                    placeholder="e.g. MBBS, MBA, BS Computer Science"
                    className="w-full p-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  />
                </div>
                {formData.role !== 'DOCTOR' && (
                  <FileInput
                    label="Qualification Certificate / Degree"
                    accept="application/pdf,image/jpeg,image/png"
                    file={qualificationFile}
                    onChange={setQualificationFile}
                    hint="PDF, JPG or PNG, max 5 MB"
                  />
                )}
              </div>
              <FileInput
                label="Resume Upload"
                accept="application/pdf"
                file={resumeFile}
                onChange={(f) => { setResumeFile(f); if (errors.resumeFile) setErrors(p => ({ ...p, resumeFile: null })); }}
                error={errors.resumeFile}
                hint="PDF, max 5 MB"
              />
            </Section>
          )}

          {/* 4) PROFESSIONAL DETAILS — Doctor-only single section that merges the old
                 Employment + Professional sections. Field order matches what the user
                 requested: Designation, Practitioner Type, Speciality, Qualification,
                 PMDC block in-line (Number + Issue + Expiry + Cert Upload),
                 Resume Upload, then Services Provided. Account Status / Practice Type /
                 Qualification Certificate are intentionally absent for Doctors — defaults
                 cover Account Status, the PMDC cert covers proof-of-qualification, and
                 Practice Type is no longer captured for the Doctor role. */}
          {formData.role === 'DOCTOR' && (
            <Section title="Professional Details" icon={Stethoscope} {...sectionProps('professional-details')}>
              {/* 1) Designation — same picker as the Employment section uses elsewhere. */}
              <SearchableSelect
                label="Designation *"
                value={formData.designation}
                onChange={(val) => { setFormData({ ...formData, designation: val }); if (errors.designation) setErrors(p => ({ ...p, designation: null })); }}
                options={designationsFor(formData.role)}
                placeholder="e.g. Consultant, Resident, Medical Officer"
                error={errors.designation}
                icon={<BadgeCheck className="h-4 w-4 text-gray-400" />}
                allowCustom
              />

              {/* 2a) Practice Type — Full Time vs Visiting. Captures whether the doctor
                  is on staff or visits the hospital for selected sessions. Was previously
                  dropped during the form-merge cleanup; reinstated per user request. */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Practice Type *</label>
                <div className="flex gap-2 items-stretch">
                  {[
                    { value: 'FULL_TIME', label: 'Full Time' },
                    { value: 'VISITING', label: 'Visiting' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setFormData({ ...formData, practiceType: opt.value }); if (errors.practiceType) setErrors(p => ({ ...p, practiceType: null })); }}
                      className={`flex-1 min-h-[3.25rem] px-3 py-2 rounded-xl text-sm font-medium border-2 transition-colors flex items-center justify-center text-center leading-tight ${
                        formData.practiceType === opt.value
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-400'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {errors.practiceType && <p className="text-red-500 text-xs mt-1">{errors.practiceType}</p>}
              </div>

              {/* 2b) Practitioner Type — two-button radio. Same item alignment pattern as
                  before so "General Practitioner" wrap behaves well next to "Specialist". */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Practitioner Type *</label>
                <div className="flex gap-2 items-stretch">
                  {[
                    { value: 'GENERAL_PRACTITIONER', label: 'General Practitioner' },
                    { value: 'SPECIALIST', label: 'Specialist' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setFormData({ ...formData, practitionerType: opt.value }); if (errors.practitionerType) setErrors(p => ({ ...p, practitionerType: null })); }}
                      className={`flex-1 min-h-[3.25rem] px-3 py-2 rounded-xl text-sm font-medium border-2 transition-colors flex items-center justify-center text-center leading-tight ${
                        formData.practitionerType === opt.value
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-400'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {errors.practitionerType && <p className="text-red-500 text-xs mt-1">{errors.practitionerType}</p>}
              </div>

              {/* 3) Speciality — same SearchableSelect as before. */}
              <SearchableSelect
                label="Speciality *"
                value={formData.specialization}
                onChange={(val) => {
                  // Store both the name (legacy text column, still rendered in several places)
                  // and the catalog id, which is what scopes the doctor's prescription pickers.
                  const match = specialities.find(s => s.name.toLowerCase() === String(val || '').trim().toLowerCase());
                  setFormData({ ...formData, specialization: val, specialityId: match ? match.id : '' });
                  if (errors.specialization) setErrors(prev => ({ ...prev, specialization: null }));
                }}
                options={specialityOptions}
                placeholder="Search specialities..."
                error={errors.specialization}
                icon={<Stethoscope className="h-4 w-4 text-gray-400" />}
              />

              {/* 4) Qualification — free-text. Doctor's "degree scan" upload is intentionally
                  not here; the PMDC certificate below already serves as proof. */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Qualification</label>
                <input
                  type="text"
                  value={formData.qualification}
                  onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                  placeholder="e.g. MBBS, FCPS, MRCP"
                  className="w-full p-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                />
              </div>

              {/* 5) PMDC block — Number, Issue, Expiry, and the Cert Upload all sit on the
                  same row per the user's spec. 4-col grid; FileInput shares the same
                  p-3.5/border-2/rounded-xl sizing as the inputs so heights line up. */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">PMDC Number *</label>
                  <input
                    type="text"
                    value={formData.pmdcNumber}
                    onChange={(e) => { setFormData({ ...formData, pmdcNumber: e.target.value }); if (errors.pmdcNumber) setErrors(p => ({ ...p, pmdcNumber: null })); }}
                    placeholder="e.g. 12345-P"
                    className={`w-full p-3.5 border-2 rounded-xl focus:ring-2 focus:ring-emerald-500 transition-all ${errors.pmdcNumber ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-emerald-500'}`}
                  />
                  {errors.pmdcNumber && <p className="text-red-500 text-xs mt-1">{errors.pmdcNumber}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Issue Date *</label>
                  <DatePicker
                    value={formData.pmdcIssueDate}
                    onChange={(v) => { setFormData({ ...formData, pmdcIssueDate: v }); if (errors.pmdcIssueDate) setErrors(p => ({ ...p, pmdcIssueDate: null })); }}
                    max={new Date().toISOString().split('T')[0]}
                    error={!!errors.pmdcIssueDate}
                    placeholder="Select issue date"
                    size="lg"
                  />
                  {errors.pmdcIssueDate && <p className="text-red-500 text-xs mt-1">{errors.pmdcIssueDate}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Expiry Date *</label>
                  <DatePicker
                    value={formData.pmdcExpiryDate}
                    onChange={(v) => { setFormData({ ...formData, pmdcExpiryDate: v }); if (errors.pmdcExpiryDate) setErrors(p => ({ ...p, pmdcExpiryDate: null })); }}
                    min={formData.pmdcIssueDate || undefined}
                    error={!!errors.pmdcExpiryDate}
                    placeholder="Select expiry date"
                    size="lg"
                  />
                  {errors.pmdcExpiryDate && <p className="text-red-500 text-xs mt-1">{errors.pmdcExpiryDate}</p>}
                </div>
                <FileInput
                  label="Upload Certificate *"
                  accept="application/pdf,image/jpeg,image/png"
                  file={pmdcFile}
                  onChange={(f) => { setPmdcFile(f); if (errors.pmdcFile) setErrors(p => ({ ...p, pmdcFile: null })); }}
                  error={errors.pmdcFile}
                />
              </div>

              {/* 6) Resume Upload — the doctor is the one role that must produce one. */}
              <FileInput
                label="Resume Upload *"
                accept="application/pdf"
                file={resumeFile}
                onChange={(f) => { setResumeFile(f); if (errors.resumeFile) setErrors(p => ({ ...p, resumeFile: null })); }}
                error={errors.resumeFile}
                hint="PDF, max 5 MB"
              />

              {/* 7) Services Provided — dropdown + Add. Each entry creates a row with an editable
                  Fee defaulted from the catalog's baseFee plus an optional commission %.
                  Backend turns each entry into a doctor-specific MedicalService copy. */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Services Provided</label>
                {medicalServices.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No services in the catalog yet - admin can add via Settings → Medical Services.</p>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <Select
                        value={servicePickerId}
                        onChange={setServicePickerId}
                        placeholder="Select a service to add…"
                        className="flex-1"
                        buttonClassName="p-3 border-2 rounded-xl"
                        options={medicalServices
                          .filter(svc => !formData.servicesProvided.some(sp => sp.serviceId === svc.id))
                          .map(svc => ({
                            value: svc.id,
                            label: svc.name + ' - default \u20a8 ' + Number(svc.baseFee || 0).toLocaleString(),
                          }))}
                      />
                      <button
                        type="button"
                        disabled={!servicePickerId}
                        onClick={() => {
                          const svc = medicalServices.find(s => s.id === servicePickerId);
                          if (!svc) return;
                          setFormData({
                            ...formData,
                            servicesProvided: [
                              ...formData.servicesProvided,
                              // Whole rupees: the cell groups bare digits, so a catalog fee must not
                              // arrive carrying a decimal part for the grouping to swallow.
                              { serviceId: svc.id, name: svc.name, fee: String(Math.round(Number(svc.baseFee || 0))), commission: '' },
                            ],
                          });
                          setServicePickerId('');
                        }}
                        className="px-4 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1.5"
                      >
                        <Plus className="h-4 w-4" /> Add
                      </button>
                    </div>

                    {formData.servicesProvided.length > 0 && (
                      <div className="mt-3 border border-gray-200 rounded-xl overflow-hidden">
                        <div className="grid grid-cols-[1fr_120px_120px_40px] gap-3 px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase">
                          <span>Service</span>
                          <span className="text-right">Fee (PKR)</span>
                          <span className="text-right">Commission %</span>
                          <span />
                        </div>
                        {formData.servicesProvided.map((row, idx) => (
                          <div key={row.serviceId} className="grid grid-cols-[1fr_120px_120px_40px] gap-3 px-4 py-2.5 border-t border-gray-100 items-center">
                            <span className="text-sm text-gray-900 truncate" title={row.name}>{row.name}</span>
                            {/* Whole rupees, grouped as they are typed, same as the salary field. */}
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              value={groupDigits(row.fee)}
                              onChange={(e) => handleAmountInput(e, String(row.fee ?? ''), (digits) => {
                                const next = [...formData.servicesProvided];
                                next[idx] = { ...row, fee: digits };
                                setFormData({ ...formData, servicesProvided: next });
                              })}
                              className="w-full px-2 py-1.5 text-sm text-right tabular-nums border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              placeholder="—"
                              value={row.commission ?? ''}
                              onChange={(e) => {
                                const next = [...formData.servicesProvided];
                                next[idx] = { ...row, commission: e.target.value };
                                setFormData({ ...formData, servicesProvided: next });
                              }}
                              className="w-full px-2 py-1.5 text-sm text-right border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  servicesProvided: formData.servicesProvided.filter((_, i) => i !== idx),
                                });
                              }}
                              className="text-gray-400 hover:text-red-500 flex justify-center"
                              title="Remove"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </Section>
          )}

          {/* 5a) PROFESSIONAL DETAILS — Nurse / Pharmacist / Technician / Dispenser merged
                 section. Same merged shape as the Doctor section above, but trimmed: only
                 the fields the user spec'd (Designation, Qualification, Certificate inline,
                 Resume Upload). For Nurse/Pharmacist the Certificate + Resume fields are
                 required; for Technician they're optional; a Dispenser gets the Pharmacist's
                 fields with no certificate at all. */}
          {['NURSE', 'PHARMACIST', 'TECHNICIAN', 'DISPENSER'].includes(formData.role) && (
            <Section title="Professional Details" icon={Stethoscope} {...sectionProps('professional-details')}>
              {/* 1) Designation */}
              <SearchableSelect
                label="Designation *"
                value={formData.designation}
                onChange={(val) => { setFormData({ ...formData, designation: val }); if (errors.designation) setErrors(p => ({ ...p, designation: null })); }}
                options={designationsFor(formData.role)}
                placeholder="e.g. Staff Nurse, Pharmacist, Lab Technician"
                error={errors.designation}
                icon={<BadgeCheck className="h-4 w-4 text-gray-400" />}
                allowCustom
              />

              {/* 2) Qualification — free text. */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Qualification</label>
                <input
                  type="text"
                  value={formData.qualification}
                  onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                  placeholder="e.g. BSN, Pharm.D, BS Medical Lab Tech"
                  className="w-full p-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                />
              </div>

              {/* 3) Certificate block — Number, Issue, Expiry, Upload all in one row.
                  Required asterisk only for credentialed roles (Nurse / Pharmacist);
                  Technician sees the same fields as optional. A Dispenser has no
                  certificate to record, so the block is not rendered for them at all. */}
              {showsCertificate && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Certificate Number{isCredentialedRole ? ' *' : ''}</label>
                  <input
                    type="text"
                    value={formData.pmdcNumber}
                    onChange={(e) => { setFormData({ ...formData, pmdcNumber: e.target.value }); if (errors.pmdcNumber) setErrors(p => ({ ...p, pmdcNumber: null })); }}
                    placeholder="e.g. PNC-12345"
                    className={`w-full p-3.5 border-2 rounded-xl focus:ring-2 focus:ring-emerald-500 transition-all ${errors.pmdcNumber ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-emerald-500'}`}
                  />
                  {errors.pmdcNumber && <p className="text-red-500 text-xs mt-1">{errors.pmdcNumber}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Issue Date{isCredentialedRole ? ' *' : ''}</label>
                  <DatePicker
                    value={formData.pmdcIssueDate}
                    onChange={(v) => { setFormData({ ...formData, pmdcIssueDate: v }); if (errors.pmdcIssueDate) setErrors(p => ({ ...p, pmdcIssueDate: null })); }}
                    max={new Date().toISOString().split('T')[0]}
                    error={!!errors.pmdcIssueDate}
                    placeholder="Select issue date"
                    size="lg"
                  />
                  {errors.pmdcIssueDate && <p className="text-red-500 text-xs mt-1">{errors.pmdcIssueDate}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Expiry Date{isCredentialedRole ? ' *' : ''}</label>
                  <DatePicker
                    value={formData.pmdcExpiryDate}
                    onChange={(v) => { setFormData({ ...formData, pmdcExpiryDate: v }); if (errors.pmdcExpiryDate) setErrors(p => ({ ...p, pmdcExpiryDate: null })); }}
                    min={formData.pmdcIssueDate || undefined}
                    error={!!errors.pmdcExpiryDate}
                    placeholder="Select expiry date"
                    size="lg"
                  />
                  {errors.pmdcExpiryDate && <p className="text-red-500 text-xs mt-1">{errors.pmdcExpiryDate}</p>}
                </div>
                <FileInput
                  label={`Upload Certificate${isCredentialedRole ? ' *' : ''}`}
                  accept="application/pdf,image/jpeg,image/png"
                  file={pmdcFile}
                  onChange={(f) => { setPmdcFile(f); if (errors.pmdcFile) setErrors(p => ({ ...p, pmdcFile: null })); }}
                  error={errors.pmdcFile}
                />
              </div>
              )}

              {/* 4) Resume Upload — a doctor is the only role that has to produce one. */}
              <FileInput
                label="Resume Upload"
                accept="application/pdf"
                file={resumeFile}
                onChange={(f) => { setResumeFile(f); if (errors.resumeFile) setErrors(p => ({ ...p, resumeFile: null })); }}
                error={errors.resumeFile}
                hint="PDF, max 5 MB"
              />
            </Section>
          )}

          {/* 5c) PROFESSIONAL DETAILS — Custom-role merged section. Shown when the
                 selected role isn't one of the 7 system roles (i.e. an admin-created
                 custom role like "Lab Manager", "Cleaner", etc.). Fields per the user
                 spec: Designation, Qualification, Educational Document Upload + Date
                 Completed + Description (all inline), Resume Upload. Educational
                 document trio uses the existing qualificationFile field plus the new
                 qualificationDateCompleted / qualificationDescription columns. */}
          {isCustomRole && (
            <Section title="Professional Details" icon={Stethoscope} {...sectionProps('professional-details')}>
              {/* 1) Designation */}
              <SearchableSelect
                label="Designation *"
                value={formData.designation}
                onChange={(val) => { setFormData({ ...formData, designation: val }); if (errors.designation) setErrors(p => ({ ...p, designation: null })); }}
                options={designationsFor(formData.role)}
                placeholder="e.g. Officer, Manager, Coordinator"
                error={errors.designation}
                icon={<BadgeCheck className="h-4 w-4 text-gray-400" />}
                allowCustom
              />

              {/* 2) Qualification — free text. */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Qualification</label>
                <input
                  type="text"
                  value={formData.qualification}
                  onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                  placeholder="e.g. BSc, BBA, Diploma in IT"
                  className="w-full p-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                />
              </div>

              {/* 3) Educational Document trio — Upload + Date Completed + Description,
                  all on a single 3-col row. All optional. */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FileInput
                  label="Educational Document"
                  accept="application/pdf,image/jpeg,image/png"
                  file={qualificationFile}
                  onChange={setQualificationFile}
                />
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date Completed</label>
                  <DatePicker
                    value={formData.qualificationDateCompleted}
                    onChange={(v) => setFormData({ ...formData, qualificationDateCompleted: v })}
                    max={new Date().toISOString().split('T')[0]}
                    placeholder="Select date completed"
                    size="lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                  <input
                    type="text"
                    value={formData.qualificationDescription}
                    onChange={(e) => setFormData({ ...formData, qualificationDescription: e.target.value })}
                    placeholder="e.g. Issued by COMSATS"
                    maxLength={500}
                    className="w-full p-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  />
                </div>
              </div>

              {/* 4) Resume Upload — optional for custom roles. */}
              <FileInput
                label="Resume Upload"
                accept="application/pdf"
                file={resumeFile}
                onChange={(f) => { setResumeFile(f); if (errors.resumeFile) setErrors(p => ({ ...p, resumeFile: null })); }}
                error={errors.resumeFile}
                hint="PDF, max 5 MB"
              />
            </Section>
          )}

          {/* 5b) CONTRACT AGREEMENT — every role. Captures monthly base salary and the
                 signed Hospital ↔ Staff agreement copy. Doctor additionally has a per-
                 service commission column in the Services Provided table above (in the
                 Professional section) since commission is service-scoped, not role-wide. */}
          {formData.role && (
            <Section title="Contract Agreement" icon={FileSignature} {...sectionProps('contract')}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Monthly Salary (PKR) *</label>
                  {/* Whole rupees, shown grouped as they are typed so a six-figure salary can be
                      read at a glance. State holds the bare digits. */}
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={groupDigits(formData.salary)}
                    onChange={(e) => handleAmountInput(e, formData.salary, (digits) => { setFormData({ ...formData, salary: digits }); if (errors.salary) setErrors(p => ({ ...p, salary: null })); })}
                    placeholder="e.g. 150,000"
                    className={`w-full p-3.5 border-2 rounded-xl tabular-nums focus:ring-2 focus:ring-emerald-500 transition-all ${errors.salary ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-emerald-500'}`}
                  />
                  {errors.salary && <p className="text-red-500 text-xs mt-1">{errors.salary}</p>}
                </div>
              </div>
              {formData.role === 'DOCTOR' && (
                <p className="text-xs text-gray-500 -mt-1">
                  Per-service commission % is captured on each row in <span className="font-medium">Services Provided</span> above.
                </p>
              )}
              <FileInput
                label="Upload Signed Contract Agreement"
                accept="application/pdf,image/jpeg,image/png"
                file={contractFile}
                onChange={setContractFile}
                hint="PDF, JPG or PNG, max 5 MB · can be uploaded later"
              />
            </Section>
          )}

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start space-x-3">
              <Lock className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Automatic Password Generation</p>
                <p className="text-xs text-amber-700 mt-1">
                  A secure temporary password will be generated automatically. You'll be able to copy and share the credentials after creating the user.
                </p>
              </div>
            </div>
          </div>
            </>
          )}
        </div>

        {/* Footer — step-aware on Add (Cancel/Back + Next/Create User); single
            Cancel + Save Changes pair on Edit. */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-3xl flex justify-between items-center">
          <button
            onClick={!isEdit && step === 'details' ? () => setStep('role') : onClose}
            className="px-6 py-3 text-gray-700 hover:bg-gray-200 rounded-xl font-semibold transition-colors"
          >
            {!isEdit && step === 'details' ? '← Back' : 'Cancel'}
          </button>
          {!isEdit && step === 'role' ? (
            <button
              onClick={() => formData.role && setStep('details')}
              disabled={!formData.role}
              className="px-8 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="px-8 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
            >
              {isEdit ? 'Save Changes' : 'Create User'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ STATUS CHANGE MODAL ============
// Captures the audit metadata for any status-changing user action — deactivate, activate,
// soft-delete, restore. Reason is a dropdown (dropdowns vary per action so audit entries
// stay consistent), description is optional free text, date is today and read-only.
// The parent handles the actual API call; this modal just gathers the form data.
const REASONS_BY_ACTION = {
  deactivate: ['Resigned', 'Terminated', 'On Leave', 'Other'],
  activate: ['Returned from Leave', 'Rehired', 'Status Correction', 'Other'],
  delete: ['Duplicate Record', 'Created in Error', 'No Longer Affiliated', 'Other'],
  restore: ['Mistakenly Deleted', 'Returning Staff', 'Status Correction', 'Other'],
};

// Per-action presentation. Decides header color/icon, submit button label/icon, and
// whether the verb reads as destructive or affirming.
const ACTION_META = {
  deactivate: { verb: 'Deactivate', destructive: true,  icon: Power,  headerTone: 'from-red-600 to-rose-700' },
  activate:   { verb: 'Activate',   destructive: false, icon: CheckCircle, headerTone: 'from-emerald-600 to-teal-700' },
  delete:     { verb: 'Delete',     destructive: true,  icon: Trash2, headerTone: 'from-red-600 to-rose-700' },
  restore:    { verb: 'Restore',    destructive: false, icon: RefreshCw, headerTone: 'from-emerald-600 to-teal-700' },
};

const StatusChangeModal = ({ user, action = 'deactivate', onClose, onSubmit }) => {
  const meta = ACTION_META[action] || ACTION_META.deactivate;
  const reasonOptions = REASONS_BY_ACTION[action] || REASONS_BY_ACTION.deactivate;
  const isDestructive = meta.destructive;
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const statusDialogRef = useDialog(onClose);

  // Format today as the read-only effective date. Localised; not sent over the wire —
  // the backend re-stamps it server-side so it can't be spoofed by a hand-crafted call.
  const today = formatClinicDate(clinicToday(), { day: 'numeric', month: 'long', year: 'numeric' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) {
      setError('Please select a reason');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ reason, description: description.trim() || null });
    } finally {
      setSubmitting(false);
    }
  };

  const HeaderIcon = meta.icon;

  return (
    <div ref={statusDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* No overflow-hidden on the shell — the Reason dropdown inside needs to be able to extend
          past it. Only the gradient header needs its corners rounded; the form below is white on
          white, so the shell's own radius already reads correctly. */}
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full">
        <div className={`bg-gradient-to-r ${meta.headerTone} px-6 py-5 rounded-t-3xl`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
                <HeaderIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{meta.verb} User</h2>
                <p className="text-white/80 text-sm">{user.fullName}</p>
              </div>
            </div>
            <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors">
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Reason *</label>
            <Select
              value={reason}
              onChange={(v) => { setReason(v); if (error) setError(null); }}
              placeholder="-- Select a reason --"
              options={reasonOptions}
              error={error ? ' ' : undefined}
              buttonClassName="p-3.5 border-2 rounded-xl"
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Add any extra context (e.g. last working day, return date, etc.)"
              maxLength={1000}
              className="w-full p-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Effective Date</label>
            <div className="flex items-center gap-2 p-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl">
              <Calendar className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-semibold text-gray-700">{today}</span>
              <span className="text-xs text-gray-500 ml-auto">Auto-set to today</span>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-gray-700 hover:bg-gray-100 rounded-xl font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`px-5 py-2.5 text-white rounded-xl font-semibold transition-colors inline-flex items-center gap-2 ${
                isDestructive
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              } disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              {submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <HeaderIcon className="h-4 w-4" />}
              {meta.verb}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============ VIEW USER MODAL ============
const ViewUserModal = ({ user, roleConfig: ROLE_CONFIG = {}, onClose, onResetPassword, onUpdateStatus, onDelete, onRestore, onEdit }) => {
  const roleConfig = ROLE_CONFIG[user.role];
  const statusConfig = STATUS_CONFIG[user.status];
  const RoleIcon = roleConfig?.icon || UserIcon;
  const [activeTab, setActiveTab] = useState('profile');
  const [zoomPic, setZoomPic] = useState(false); // profile-photo lightbox open?

  // Doctor / Nurse / Pharmacist share the same credentialed block (DOB, CNIC, licence,
  // certificate file) — only the label differs (PMDC for Doctor, generic for the others).
  const isCredentialedRole = ['DOCTOR', 'NURSE', 'PHARMACIST'].includes(user.role);
  const certLabel = user.role === 'DOCTOR' ? 'PMDC' : 'Certificate';
  // Custom (admin-created) role — mirrors the Add User flow's logic so the View modal
  // shows the same field shape that was captured at creation time.
  const SYSTEM_ROLES_LOCAL = ['ADMIN', 'ACCOUNTS', 'DOCTOR', 'NURSE', 'PHARMACIST', 'RECEPTIONIST', 'TECHNICIAN', 'DISPENSER'];
  const isCustomRole = user.role && !SYSTEM_ROLES_LOCAL.includes(user.role);

  // File URLs come back from the API as relative paths (/files/...). REACT_APP_API_URL
  // includes the `/api` suffix in production, so strip it to get the host root the
  // uploaded files live under. Mirrors the pattern the previous modal used.
  const apiHost = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/api', '')
    : 'http://localhost:8080';
  // Append the JWT as a ?token= query param so a plain <a href> / browser navigation to an
  // auth-protected file works (the browser can't attach an Authorization header on a top-level
  // navigation). The JWT filter accepts ?token= on GET requests — same approach the Patient
  // documents view uses, kept consistent here.
  const authToken = localStorage.getItem('clinic_auth_token');
  const withToken = (url) => `${url}${url.includes('?') ? '&' : '?'}token=${authToken}`;

  // Audit log — lazy-loaded the first time the Activity tab opens. Newest first.
  const [auditLog, setAuditLog] = useState(null); // null = not loaded yet
  const [auditLoading, setAuditLoading] = useState(false);
  const loadAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const data = await userAPI.getAuditLog(user.id);
      setAuditLog(Array.isArray(data) ? data : []);
    } catch {
      setAuditLog([]);
    } finally {
      setAuditLoading(false);
    }
  }, [user.id]);
  useEffect(() => {
    if (activeTab === 'activity' && auditLog === null) loadAuditLog();
  }, [activeTab, auditLog, loadAuditLog]);
  const viewUserDialogRef = useDialog(onClose, { confirmDiscard: false });

  // Build the document list from whichever URL fields the user has on file. Each tile
  // gets its own icon + colour so the Documents tab reads as a recognisable grid.
  const documents = [
    user.profileImageUrl && {
      key: 'profile', label: 'Profile Picture', icon: ImageIcon, color: 'pink',
      url: apiHost + user.profileImageUrl,
    },
    user.pmdcCertificateUrl && {
      key: 'pmdc', label: `${certLabel} Certificate`, icon: BadgeCheck, color: 'emerald',
      url: apiHost + user.pmdcCertificateUrl,
    },
    user.resumeUrl && {
      key: 'resume', label: 'Resume / CV', icon: FileText, color: 'blue',
      url: apiHost + user.resumeUrl,
    },
    user.qualificationFileUrl && {
      key: 'qualification', label: 'Qualification Certificate', icon: GraduationCap, color: 'indigo',
      url: apiHost + user.qualificationFileUrl,
    },
    user.contractFileUrl && {
      key: 'contract', label: 'Signed Contract', icon: FileSignature, color: 'emerald',
      url: apiHost + user.contractFileUrl,
    },
    user.certificationsFileUrl && {
      key: 'certifications', label: 'Certifications', icon: BadgeCheck, color: 'teal',
      url: apiHost + user.certificationsFileUrl,
    },
    user.cnicDocumentUrl && {
      key: 'cnic', label: 'CNIC - Front', icon: Shield, color: 'amber',
      url: apiHost + user.cnicDocumentUrl,
    },
    user.cnicBackDocumentUrl && {
      key: 'cnic-back', label: 'CNIC - Back', icon: Shield, color: 'amber',
      url: apiHost + user.cnicBackDocumentUrl,
    },
  ].filter(Boolean);

  const formatDate = (d) =>
    formatClinicDate(d, { day: 'numeric', month: 'long', year: 'numeric' }, null);
  const formatDateTime = (d) => formatClinicDateTime(d, {}, null);

  const initials = (user.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)) || 'U';

  return (
    <div ref={viewUserDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* Full-size profile-photo lightbox (above this modal); opened by clicking the avatar. */}
      <ImageLightbox
        src={zoomPic ? authedMediaUrl(user.profileImageUrl) : null}
        alt={user.fullName}
        onClose={() => setZoomPic(false)}
      />
      <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden">

        {/* Sticky header — flat emerald gradient */}
        <div className="flex-shrink-0 relative bg-gradient-to-r from-navy-800 to-navy-700 text-white overflow-hidden">
          <button aria-label="Close" onClick={onClose} className="absolute top-4 right-4 z-10 p-2 hover:bg-white/20 rounded-xl transition-colors">
            <X className="h-5 w-5 text-white" />
          </button>
          <div className="relative p-8">
            <div className="flex items-center space-x-6">
              {/* Avatar — uses the profile picture if uploaded, otherwise initials.
                  When a photo exists, clicking opens a full-size lightbox. */}
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center border-2 border-white border-opacity-30 overflow-hidden flex-shrink-0">
                {user.profileImageUrl ? (
                  <button type="button" onClick={() => setZoomPic(true)} aria-label="Enlarge photo" title="Click to enlarge" className="block w-full h-full focus:outline-none focus-visible:outline-emerald-500 focus-visible:-outline-offset-2">
                    {/* Outline, not ring: this box's overflow-hidden would clip a ring. */}
                    <img src={authedMediaUrl(user.profileImageUrl)} alt={user.fullName} className="w-full h-full object-cover cursor-zoom-in" />
                  </button>
                ) : (
                  <span className="text-3xl font-bold text-white">
                    {initials}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-4xl font-bold mb-2 text-white">
                  {user.fullName}
                </h1>
                <div className="flex items-center flex-wrap gap-x-4 gap-y-1.5">
                  <div className="flex items-center space-x-2">
                    <UserCog className="h-5 w-5 opacity-70" />
                    <span className="text-lg font-semibold text-white font-mono">
                      @{user.username}
                    </span>
                  </div>
                  {/* Role pill — solid dark badge using the role's gradient (Doctor=blue,
                      Admin=red, Nurse=pink, etc.). Dark + saturated so it reads as a
                      distinct "badge" rather than a light chip. */}
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold border border-white/40 shadow-md inline-flex items-center gap-1.5 text-white bg-gradient-to-r ${roleConfig?.color || 'from-slate-500 to-slate-600'}`}>
                    <RoleIcon className="h-3.5 w-3.5" />
                    {roleConfig?.label || user.role}
                  </span>
                  {(() => {
                    // Status pill — same semantics as the table (STATUS_CONFIG):
                    // emerald for Active, slate for inactive, rose for suspended.
                    // Tiny dot keeps the semantic cue.
                    const tone = {
                      ACTIVE:    'bg-emerald-100 border-emerald-300 text-emerald-800',
                      INACTIVE:  'bg-slate-200 border-slate-300 text-slate-800',
                      SUSPENDED: 'bg-rose-200 border-rose-300 text-rose-900',
                    }[user.status] || 'bg-slate-200 border-slate-300 text-slate-800';
                    const dot = {
                      ACTIVE:    'bg-emerald-500',
                      INACTIVE:  'bg-slate-500',
                      SUSPENDED: 'bg-rose-600',
                    }[user.status] || 'bg-slate-500';
                    return (
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold border shadow-sm inline-flex items-center gap-1.5 ${tone}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                        {statusConfig?.label || user.status}
                      </span>
                    );
                  })()}
                </div>
                {user.designation && (
                  <p className="text-white text-opacity-90 text-sm mt-1.5 flex items-center gap-2">
                    <Briefcase className="h-4 w-4 opacity-70" />
                    <span className="font-semibold">
                      {user.designation}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs — Profile (default) and Documents. Same pill style as the patient modal */}
        <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-3">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'profile',   label: 'Profile',   icon: <UserIcon className="h-4 w-4" />, badge: null },
              { key: 'documents', label: 'Documents', icon: <FileText className="h-4 w-4" />, badge: documents.length },
              { key: 'activity',  label: 'Activity',  icon: <Clock className="h-4 w-4" />,    badge: auditLog ? auditLog.length : null },
            ].map(tab => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                    isActive
                      ? 'bg-emerald-600 text-white shadow-md shadow-emerald-200/60'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                  {tab.badge !== null && tab.badge > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                      isActive ? 'bg-white/25 text-white' : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-8">
          {activeTab === 'profile' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

              {/* Personal Information — matches the Add modal's Personal section: identity,
                  DOB, CNIC, Languages, plus Email + Phone (which the form folds into
                  Personal too). One card here mirrors one accordion there. */}
              <UserInfoCard
                title="Personal Information"
                icon={UserIcon}
                gradient="from-blue-50 to-indigo-50"
                border="border-blue-100"
                iconBg="from-blue-500 to-indigo-600"
              >
                <UserRow icon={<UserIcon className="h-5 w-5 text-blue-600" />} label="Full Name" value={user.fullName} highlight />
                {user.title && (
                  <UserRow icon={<BadgeCheck className="h-5 w-5 text-blue-600" />} label="Title" value={user.title} />
                )}
                <UserRow icon={<Calendar className="h-5 w-5 text-purple-600" />} label="Date of Birth" value={formatDate(user.dateOfBirth)} />
                <UserRow
                  icon={<Shield className="h-5 w-5 text-emerald-600" />}
                  label="CNIC"
                  value={user.cnic ? (
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <span>{user.cnic}</span>
                      <FilePreviewEye url={user.cnicDocumentUrl} label="Front" />
                      <FilePreviewEye url={user.cnicBackDocumentUrl} label="Back" />
                    </span>
                  ) : null}
                  mono
                />
                <UserRow icon={<Globe className="h-5 w-5 text-teal-600" />} label="Languages" value={user.languagesSpoken} />
                <UserRow icon={<Mail className="h-5 w-5 text-emerald-600" />} label="Email" value={user.email} />
                <UserRow icon={<Phone className="h-5 w-5 text-teal-600" />} label="Phone" value={user.phone} />
              </UserInfoCard>

              {/* Account — the extra card the user asked for (no equivalent accordion in
                  the Add form). Read-only system metadata: username, role, when the
                  account was created, and last login time. Designation lives in
                  Professional Details below to mirror the form. */}
              <UserInfoCard
                title="Account"
                icon={UserCog}
                gradient="from-amber-50 to-orange-50"
                border="border-amber-100"
                iconBg="from-amber-500 to-orange-600"
              >
                <UserRow icon={<UserCog className="h-5 w-5 text-amber-600" />} label="Username" value={'@' + user.username} mono highlight />
                <UserRow icon={<RoleIcon className="h-5 w-5 text-orange-600" />} label="Role" value={roleConfig?.label || user.role} />
                <UserRow icon={<Clock className="h-5 w-5 text-amber-600" />} label="Joined" value={formatDate(user.createdAt)} />
                <UserRow icon={<Clock className="h-5 w-5 text-orange-600" />} label="Last Login" value={formatDateTime(user.lastLogin)} />
              </UserInfoCard>

              {/* Professional Details — role-conditional card that mirrors whichever
                  merged section the Add User flow captured. One consolidated card per
                  user so the View modal stays aligned with the form. Field order matches
                  the form exactly. Account Status is dropped from this card body since
                  the header pill already shows it. */}
              {user.role === 'DOCTOR' && (
                <UserInfoCard
                  title="Professional Details"
                  icon={Stethoscope}
                  gradient="from-violet-50 to-purple-50"
                  border="border-violet-100"
                  iconBg="from-violet-500 to-purple-600"
                  wide
                >
                  <UserRow icon={<Briefcase className="h-5 w-5 text-violet-600" />} label="Designation" value={user.designation} />
                  <UserRow icon={<BadgeCheck className="h-5 w-5 text-purple-600" />} label="Practice Type" value={user.practiceType ? user.practiceType.replace(/_/g, ' ').toLowerCase() : null} />
                  <UserRow icon={<BadgeCheck className="h-5 w-5 text-violet-600" />} label="Practitioner Type" value={user.practitionerType ? user.practitionerType.replace(/_/g, ' ').toLowerCase() : null} />
                  <UserRow icon={<Stethoscope className="h-5 w-5 text-violet-600" />} label="Speciality" value={user.specialization} />
                  <UserRow icon={<GraduationCap className="h-5 w-5 text-indigo-600" />} label="Qualification" value={user.qualification} />
                  <UserRow icon={<Shield className="h-5 w-5 text-violet-600" />} label="PMDC Number" value={user.pmdcNumber} mono />
                  <UserRow icon={<Calendar className="h-5 w-5 text-purple-600" />} label="Issue Date" value={formatDate(user.pmdcIssueDate)} />
                  <UserRow icon={<Calendar className="h-5 w-5 text-rose-600" />} label="Expiry Date" value={formatDate(user.pmdcExpiryDate)} />
                  {/* Services Provided — sits inside Professional Details to mirror the
                      Add form. Spans both inner columns of the wide card so the fee +
                      commission table doesn't get cramped at half-width. */}
                  {Array.isArray(user.servicesProvided) && user.servicesProvided.length > 0 && (
                    <div className="lg:col-span-2">
                      <div className="text-xs uppercase tracking-wide font-semibold text-violet-700 mb-2 mt-2 px-1">Services Provided</div>
                      <div className="bg-white bg-opacity-70 rounded-xl border border-violet-100 overflow-hidden">
                        {user.servicesProvided.map(s => (
                          <div key={s.id} className="flex items-center justify-between gap-3 px-4 py-3 border-t border-violet-100 first:border-t-0 text-sm">
                            <span className="text-gray-900 font-medium truncate">{s.name}</span>
                            <div className="flex items-center gap-3 shrink-0">
                              <span className="font-mono font-semibold text-emerald-700">PKR {Number(s.baseFee || 0).toLocaleString()}</span>
                              {s.commission != null && (
                                <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200" title="Doctor commission">
                                  {Number(s.commission)}%
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </UserInfoCard>
              )}

              {['NURSE', 'PHARMACIST', 'TECHNICIAN', 'DISPENSER'].includes(user.role) && (
                <UserInfoCard
                  title="Professional Details"
                  icon={Stethoscope}
                  gradient="from-violet-50 to-purple-50"
                  border="border-violet-100"
                  iconBg="from-violet-500 to-purple-600"
                  wide
                >
                  <UserRow icon={<Briefcase className="h-5 w-5 text-violet-600" />} label="Designation" value={user.designation} />
                  <UserRow icon={<GraduationCap className="h-5 w-5 text-indigo-600" />} label="Qualification" value={user.qualification} />
                  {/* A dispenser has no certificate, so those rows are left out entirely. */}
                  {user.role !== 'DISPENSER' && (
                    <>
                      <UserRow icon={<Shield className="h-5 w-5 text-violet-600" />} label="Certificate Number" value={user.pmdcNumber} mono />
                      <UserRow icon={<Calendar className="h-5 w-5 text-purple-600" />} label="Issue Date" value={formatDate(user.pmdcIssueDate)} />
                      <UserRow icon={<Calendar className="h-5 w-5 text-rose-600" />} label="Expiry Date" value={formatDate(user.pmdcExpiryDate)} />
                    </>
                  )}
                </UserInfoCard>
              )}

              {isCustomRole && (
                <UserInfoCard
                  title="Professional Details"
                  icon={Stethoscope}
                  gradient="from-violet-50 to-purple-50"
                  border="border-violet-100"
                  iconBg="from-violet-500 to-purple-600"
                  wide
                >
                  <UserRow icon={<Briefcase className="h-5 w-5 text-violet-600" />} label="Designation" value={user.designation} />
                  <UserRow icon={<GraduationCap className="h-5 w-5 text-indigo-600" />} label="Qualification" value={user.qualification} />
                  <UserRow icon={<Calendar className="h-5 w-5 text-purple-600" />} label="Date Completed" value={formatDate(user.qualificationDateCompleted)} />
                  <UserRow icon={<FileText className="h-5 w-5 text-indigo-600" />} label="Description" value={user.qualificationDescription} />
                </UserInfoCard>
              )}

              {/* Employment — Admin/Accounts/Receptionist (the roles whose Add form keeps
                  the separate Employment section). Simpler card; just designation +
                  qualification since Account Status is in the header pill already. */}
              {['ADMIN', 'ACCOUNTS', 'RECEPTIONIST'].includes(user.role) && (
                <UserInfoCard
                  title="Employment"
                  icon={Briefcase}
                  gradient="from-slate-50 to-gray-100"
                  border="border-slate-200"
                  iconBg="from-slate-500 to-gray-600"
                  wide
                >
                  <UserRow icon={<Briefcase className="h-5 w-5 text-slate-600" />} label="Designation" value={user.designation} />
                  <UserRow icon={<GraduationCap className="h-5 w-5 text-indigo-600" />} label="Qualification" value={user.qualification} />
                </UserInfoCard>
              )}

              {/* Contract Agreement — universal (every role has a salary on file). */}
              {(user.salary != null) && (
                <UserInfoCard
                  title="Contract Agreement"
                  icon={FileSignature}
                  gradient="from-rose-50 to-pink-50"
                  border="border-rose-100"
                  iconBg="from-rose-500 to-pink-600"
                >
                  <UserRow icon={<FileSignature className="h-5 w-5 text-rose-600" />} label="Monthly Salary" value={'PKR ' + Number(user.salary).toLocaleString()} mono highlight />
                </UserInfoCard>
              )}
            </div>
          ) : activeTab === 'documents' ? (
            // Documents tab — compact table, mirroring the Patient documents view so both
            // surfaces read the same way. View opens the file in a new tab (auth via the
            // ?token= query param the JWT filter accepts on GET).
            <div>
              {documents.length === 0 ? (
                <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No documents uploaded</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Uploaded files (certificate, resume, contract, qualification, etc.) will appear here.
                  </p>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
                        <th className="px-3 py-2.5 w-14"></th>
                        <th className="px-3 py-2.5">Name</th>
                        <th className="px-3 py-2.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {documents.map(doc => {
                        const DocIcon = doc.icon;
                        const isPdf = /\.pdf(\?|$)/i.test(doc.url);
                        return (
                          <tr key={doc.key} className="hover:bg-gray-50/60">
                            <td className="px-3 py-2">
                              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                                {DocIcon ? <DocIcon className="h-5 w-5" /> : (isPdf ? <FileText className="h-5 w-5" /> : <ImageIcon className="h-5 w-5" />)}
                              </div>
                            </td>
                            <td className="px-3 py-2 max-w-xs">
                              <div className="font-medium text-gray-900 truncate">{doc.label}</div>
                              <div className="text-xs text-gray-500">Uploaded document</div>
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <a
                                href={withToken(doc.url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-600 hover:text-emerald-700 inline-flex"
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            // Activity tab — reverse-chronological audit timeline. Each entry shows what
            // changed (field + old → new), who, when, and the reason/description for
            // status changes. Lazy-loaded the first time the tab is opened.
            <ActivityTimeline log={auditLog} loading={auditLoading} />
          )}
        </div>

        {/* Footer — For deleted users, only Restore + Close are meaningful (no point
            offering Deactivate/Delete-again or Reset Password on a soft-deleted record).
            For active users, the full action set is shown. */}
        <div className="flex-shrink-0 px-8 py-5 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          {user.status === 'DELETED' ? (
            <>
              <div>
                {onRestore && (
                  <button
                    onClick={onRestore}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Restore User
                  </button>
                )}
              </div>
              <button onClick={onClose} className="px-5 py-2 bg-gray-200 text-gray-800 rounded-xl font-semibold hover:bg-gray-300 transition-colors">
                Close
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onUpdateStatus(user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                  className={`px-4 py-2 ${user.status === 'ACTIVE' ? 'text-red-600 hover:bg-red-50' : 'text-emerald-600 hover:bg-emerald-50'} rounded-xl font-medium transition-colors flex items-center gap-2`}
                >
                  <Power className="h-4 w-4" />
                  {user.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                </button>
                {onDelete && (
                  <button
                    onClick={onDelete}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl font-medium transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                )}
              </div>
              <div className="flex space-x-3">
                {onEdit && (
                  <button onClick={onEdit} className="px-4 py-2 text-emerald-700 hover:bg-emerald-50 rounded-xl font-medium transition-colors flex items-center gap-2 border border-emerald-200">
                    <Edit2 className="h-4 w-4" />
                    Edit
                  </button>
                )}
                <button onClick={onResetPassword} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-xl font-medium transition-colors flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Reset Password
                </button>
                <button onClick={onClose} className="px-5 py-2 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors">
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ CREDENTIALS MODAL ============
const CredentialsModal = ({ user, roleConfig: ROLE_CONFIG = {}, onClose }) => {
  const [copied, setCopied] = useState({ username: false, password: false });
  // The temporary password is shown only once, so a stray Escape must not dismiss it; Done does.
  const credentialsDialogRef = useDialog(onClose, { escape: false });

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [field]: true });
    setTimeout(() => setCopied({ ...copied, [field]: false }), 2000);
  };

  const roleConfig = ROLE_CONFIG[user.role];

  return (
    <div ref={credentialsDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-8 text-center">
          <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <CheckCircle className="h-12 w-12 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold text-white">User Created Successfully!</h2>
          <p className="text-white/80 mt-2">Share these credentials securely with the user</p>
        </div>

        <div className="p-8">
          <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl mb-6">
            <div className={`w-14 h-14 bg-gradient-to-br ${roleConfig?.color || 'from-gray-400 to-gray-500'} rounded-xl flex items-center justify-center`}>
              <span className="text-white font-bold text-lg">
                {user.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
              </span>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{user.fullName}</h3>
              <span className={`text-sm ${roleConfig?.textColor || 'text-gray-600'}`}>
                {roleConfig?.label || user.role}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
              <div className="flex items-center space-x-2">
                <div className="flex-1 p-4 bg-emerald-50 border border-emerald-200 rounded-xl font-mono font-bold text-emerald-700">
                  @{user.username}
                </div>
                <button
                  onClick={() => copyToClipboard(user.username, 'username')}
                  className={`p-4 rounded-xl transition-all ${copied.username ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {copied.username ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Temporary Password</label>
              <div className="flex items-center space-x-2">
                <div className="flex-1 p-4 bg-amber-50 border border-amber-200 rounded-xl font-mono font-bold text-amber-700">
                  {user.tempPassword}
                </div>
                <button
                  onClick={() => copyToClipboard(user.tempPassword, 'password')}
                  className={`p-4 rounded-xl transition-all ${copied.password ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {copied.password ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Important Security Notice</p>
                <p className="text-xs text-red-700 mt-1">
                  This temporary password will only be shown once. The user must change it on their first login.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 bg-gray-50 rounded-b-3xl">
          <button
            onClick={onClose}
            className="w-full py-4 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ USER PERMISSIONS MODAL ============
// Catalog-driven: pulls MODULE_CONFIG / ACTION_CONFIG from RolesPermissions.js so the
// per-user override surface shows the exact same modules and sub-actions (row actions,
// detail tabs, etc.) that the Roles page does. Toggles overridden against the role
// default get an amber ring so the admin can see at a glance what's diverged.
const MODULE_KEYS = Object.keys(PERM_MODULE_CONFIG);

const UserPermissionsModal = ({ user, onClose, onSaved }) => {
  const [rolePerms, setRolePerms] = useState({});    // baseline from role
  const [effective, setEffective] = useState({});     // what user actually has
  const [overrides, setOverrides] = useState({});     // only the diffs
  const [catalog, setCatalog] = useState({});         // module → action[] from backend
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Which collapsible menu sections are expanded (same grouping as the Roles page).
  const [expandedGroups, setExpandedGroups] = useState([]);
  const toggleGroup = (id) =>
    setExpandedGroups(prev => (prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]));
  const expandAllGroups = () => setExpandedGroups(PERM_MODULE_GROUPS.map(g => g.id));
  const collapseAllGroups = () => setExpandedGroups([]);

  // Action list for a module — backend catalog if present, else base 4. Same fallback as
  // RolesPermissions.actionsFor so the two screens behave identically during a partial
  // deploy where backend and frontend are out of sync.
  const actionsFor = useCallback(
    (moduleName) => {
      const fromCatalog = catalog[moduleName];
      const actions =
        Array.isArray(fromCatalog) && fromCatalog.length > 0
          ? fromCatalog
          : PERM_BASE_ACTIONS;
      // Same parked-feature filter as RolesPermissions.actionsFor — the two screens must offer
      // exactly the same toggles.
      return actions.filter(a => !HIDDEN_PERMISSION_ACTIONS.includes(a));
    },
    [catalog],
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Load role permissions, overrides, and the action catalog in parallel.
        const [roleData, overrideData, catalogData] = await Promise.all([
          rolePermissionAPI.getByRole(user.role).catch(() => ({})),
          userAPI.getPermissionOverrides(user.id).catch(() => ({})),
          rolePermissionAPI.getCatalog().catch(() => ({})),
        ]);
        const roleParsed = roleData?.permissions || roleData || {};
        const overrideParsed = overrideData && !overrideData.error ? overrideData : {};
        const catalogParsed = catalogData && typeof catalogData === 'object' ? catalogData : {};

        // Build effective = role permission + overrides applied on top. Computed against
        // the catalog so any new sub-action (e.g. VIEW_DOCUMENTS_TAB) appears immediately
        // even if the role hasn't been re-seeded yet (default = role baseline).
        const effectiveParsed = {};
        MODULE_KEYS.forEach(modKey => {
          effectiveParsed[modKey] = {};
          const acts =
            Array.isArray(catalogParsed[modKey]) && catalogParsed[modKey].length > 0
              ? catalogParsed[modKey]
              : PERM_BASE_ACTIONS;
          acts.forEach(act => {
            let val = roleParsed[modKey]?.[act] ?? false;
            if (overrideParsed[modKey]?.[act] !== undefined) {
              val = overrideParsed[modKey][act];
            }
            effectiveParsed[modKey][act] = val;
          });
        });

        setRolePerms(roleParsed);
        setEffective(effectiveParsed);
        setOverrides(overrideParsed);
        setCatalog(catalogParsed);
      } catch (err) { console.error('Failed to load permissions:', err); setError('Failed to load permissions'); }
      finally { setLoading(false); }
    })();
  }, [user.id, user.role]);
  // The switches are buttons, which the hook cannot see, so a flip is recorded here instead.
  const flippedRef = useRef(false);
  const permissionsDialogRef = useDialog(onClose, { isDirty: () => flippedRef.current });

  const isOverridden = (mod, act) => overrides[mod]?.[act] !== undefined;
  const getValue = (mod, act) => effective[mod]?.[act] || false;
  const getRoleValue = (mod, act) => rolePerms[mod]?.[act] || false;

  const toggle = (mod, act) => {
    flippedRef.current = true;
    const current = getValue(mod, act);
    const newVal = !current;
    setEffective(prev => ({
      ...prev,
      [mod]: { ...prev[mod], [act]: newVal },
    }));
    // Track if this differs from role
    const roleVal = getRoleValue(mod, act);
    if (newVal !== roleVal) {
      setOverrides(prev => ({
        ...prev,
        [mod]: { ...prev[mod], [act]: newVal },
      }));
    } else {
      // Remove override — matches role
      setOverrides(prev => {
        const copy = { ...prev };
        if (copy[mod]) {
          const { [act]: _, ...rest } = copy[mod];
          if (Object.keys(rest).length === 0) delete copy[mod];
          else copy[mod] = rest;
        }
        return copy;
      });
    }
  };

  // Bulk grant/deny every action in a module — keeps the master switch responsive and
  // makes sure overrides stay in sync: any action that ends up matching the role default
  // drops out of the overrides map, any that differ get added.
  const bulkToggle = (mod, newVal) => {
    flippedRef.current = true;
    const acts = actionsFor(mod);
    setEffective(prev => {
      const next = { ...prev, [mod]: { ...prev[mod] } };
      acts.forEach(a => { next[mod][a] = newVal; });
      return next;
    });
    setOverrides(prev => {
      const next = { ...prev };
      const modNext = { ...(next[mod] || {}) };
      acts.forEach(a => {
        const roleVal = rolePerms[mod]?.[a] ?? false;
        if (newVal !== roleVal) modNext[a] = newVal;
        else delete modNext[a];
      });
      if (Object.keys(modNext).length === 0) delete next[mod];
      else next[mod] = modNext;
      return next;
    });
  };

  // Grant/deny every action across every module in a menu section in one go, keeping the
  // override map in sync (any value matching the role default drops out).
  const bulkToggleGroup = (group, newVal) => {
    flippedRef.current = true;
    const visibleModules = group.modules.filter(m => !HIDDEN_PERMISSION_MODULES.includes(m));
    setEffective(prev => {
      const next = { ...prev };
      visibleModules.forEach(mod => {
        next[mod] = { ...next[mod] };
        actionsFor(mod).forEach(a => { next[mod][a] = newVal; });
      });
      return next;
    });
    setOverrides(prev => {
      const next = { ...prev };
      visibleModules.forEach(mod => {
        const modNext = { ...(next[mod] || {}) };
        actionsFor(mod).forEach(a => {
          const roleVal = rolePerms[mod]?.[a] ?? false;
          if (newVal !== roleVal) modNext[a] = newVal;
          else delete modNext[a];
        });
        if (Object.keys(modNext).length === 0) delete next[mod];
        else next[mod] = modNext;
      });
      return next;
    });
  };

  // Whole-user master switch — grant/deny every action across every visible module by
  // running the per-section bulk toggle over all sections (keeps the override map in sync).
  const bulkToggleAll = (newVal) => {
    PERM_MODULE_GROUPS.forEach(group => bulkToggleGroup(group, newVal));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await userAPI.updatePermissionOverrides(user.id, effective);
      if (result?.error) { setError(result.error); return; }
      // If editing own permissions, refresh the app's permission state
      const currentUser = JSON.parse(localStorage.getItem('clinic_auth_user') || '{}');
      if (currentUser.id === user.id) {
        window.dispatchEvent(new Event('permissions:changed'));
      }
      onSaved();
    } catch (err) { setError(err.message || 'Failed to save'); console.error(err); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await userAPI.clearPermissionOverrides(user.id);
      // Saved on the spot, so nothing is left to lose.
      flippedRef.current = false;
      setOverrides({});
      const roleData = await rolePermissionAPI.getByRole(user.role);
      setEffective(roleData.permissions || {});
      const currentUser = JSON.parse(localStorage.getItem('clinic_auth_user') || '{}');
      if (currentUser.id === user.id) {
        window.dispatchEvent(new Event('permissions:changed'));
      }
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const overrideCount = Object.values(overrides).reduce((sum, mod) => sum + Object.keys(mod).length, 0);

  // True only when every visible action of every module is granted — drives the header master switch.
  const allPermissionsGranted = (() => {
    let any = false, all = true;
    PERM_MODULE_GROUPS.forEach(group =>
      group.modules
        .filter(m => !HIDDEN_PERMISSION_MODULES.includes(m))
        .forEach(m => actionsFor(m).forEach(a => {
          any = true;
          if (!getValue(m, a)) all = false;
        })));
    return any && all;
  })();

  return (
    <div ref={permissionsDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Edit Permissions</h2>
            <p className="text-white/70 text-sm">
              {user.fullName} - Role: {user.role}
              {overrideCount > 0 && <span className="ml-2 px-2 py-0.5 bg-amber-400 text-amber-900 text-xs font-bold rounded-full">{overrideCount} override{overrideCount !== 1 ? 's' : ''}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Whole-user master switch — grant/deny every permission in every module.
                Label shows the action a click performs ("Grant all" / "Deny all"). */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-lg">
              <span className="hidden md:inline text-white text-sm font-medium">
                {allPermissionsGranted ? 'Deny all' : 'Grant all'}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={allPermissionsGranted}
                onClick={() => bulkToggleAll(!allPermissionsGranted)}
                title={allPermissionsGranted ? 'Deny all permissions' : 'Grant all permissions'}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent flex-shrink-0 ${allPermissionsGranted ? 'bg-white' : 'bg-white/30'}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full shadow-sm transition-transform duration-200 ${allPermissionsGranted ? 'translate-x-5 bg-gradient-to-r from-navy-800 to-navy-700' : 'translate-x-0.5 bg-white'}`} />
              </button>
            </div>
            <button
              onClick={expandAllGroups}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors"
              title="Expand all sections"
            >
              <ChevronsUpDown className="w-4 h-4" />
              <span className="hidden sm:inline">Expand all</span>
            </button>
            <button
              onClick={collapseAllGroups}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors"
              title="Collapse all sections"
            >
              <ChevronsDownUp className="w-4 h-4" />
              <span className="hidden sm:inline">Collapse all</span>
            </button>
            <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X className="h-5 w-5 text-white" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-12 text-gray-400"><RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />Loading...</div>
          ) : error ? (
            <div className="m-6 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg">{error}</div>
          ) : (
            <div className="p-4 sm:p-5 space-y-3">
              {PERM_MODULE_GROUPS.map((group) => {
                const GroupIcon = group.icon;
                const isOpen = expandedGroups.includes(group.id);
                // Drop modules hidden behind a parked feature flag; skip the section if empty
                // (see config/features.js).
                const modules = group.modules.filter(m => !HIDDEN_PERMISSION_MODULES.includes(m));
                if (modules.length === 0) return null;
                const isMulti = modules.length > 1;

                // Section-wide tallies: granted count and how many actions diverge from the
                // user's role default (overrides) — surfaced on the collapsed header so the
                // admin sees at a glance which sections have customisations.
                let grantedCount = 0, totalCount = 0, overrideInGroup = 0;
                modules.forEach(m => {
                  actionsFor(m).forEach(a => {
                    totalCount++;
                    if (getValue(m, a)) grantedCount++;
                    if (isOverridden(m, a)) overrideInGroup++;
                  });
                });
                const groupAllGranted = totalCount > 0 && grantedCount === totalCount;

                return (
                  <div key={group.id} className="border border-gray-200 rounded-2xl bg-white overflow-hidden">
                    {/* Section header — click the title to expand/collapse. */}
                    <div className={`flex items-center gap-3 px-4 py-3 transition-colors ${isOpen ? 'bg-gray-50 border-b border-gray-100' : 'hover:bg-gray-50'}`}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.id)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                        aria-expanded={isOpen}
                      >
                        {isOpen
                          ? <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          : <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                        <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <GroupIcon className="w-5 h-5 text-gray-600" />
                        </div>
                        <span className="font-semibold text-gray-800 truncate">{group.label}</span>
                        {isMulti && <span className="text-xs font-medium text-gray-400 flex-shrink-0">{modules.length} items</span>}
                        {overrideInGroup > 0 && (
                          <span className="text-[11px] font-bold px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full flex-shrink-0" title={`${overrideInGroup} action(s) overridden from role`}>
                            {overrideInGroup} override{overrideInGroup !== 1 ? 's' : ''}
                          </span>
                        )}
                      </button>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${grantedCount > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                        {grantedCount}/{totalCount}
                      </span>
                      <button
                        type="button"
                        onClick={() => bulkToggleGroup(group, !groupAllGranted)}
                        title={groupAllGranted ? `Deny all in ${group.label}` : `Grant all in ${group.label}`}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 flex-shrink-0 ${groupAllGranted ? 'bg-gradient-to-r from-navy-800 to-navy-700' : 'bg-gray-300'}`}
                        role="switch"
                        aria-checked={groupAllGranted}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${groupAllGranted ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>

                    {/* Section body — shown only when expanded. */}
                    {isOpen && (
                      <div className="divide-y divide-gray-100">
                        {modules.map((modKey) => {
                          const modConf = PERM_MODULE_CONFIG[modKey];
                          if (!modConf) return null;
                          const ModIcon = modConf.icon;
                          const modActions = actionsFor(modKey);

                          // Group action keys by `group` so each module renders with labelled
                          // Basic / Row actions / Detail tabs sections.
                          const byGroup = { base: [], row: [], tab: [] };
                          for (const a of modActions) {
                            const g = PERM_ACTION_CONFIG[a]?.group || 'base';
                            if (byGroup[g]) byGroup[g].push(a);
                          }
                          const hasExtras = byGroup.row.length > 0 || byGroup.tab.length > 0;

                          const renderToggle = (action) => {
                            const conf = PERM_ACTION_CONFIG[action] || permFallbackActionConfig(action);
                            const ActionIcon = conf.icon;
                            const isGranted = getValue(modKey, action);
                            const overridden = isOverridden(modKey, action);
                            return (
                              <label
                                key={action}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                                  isGranted ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                                } ${overridden ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                                title={overridden ? `Overridden (role default: ${getRoleValue(modKey, action) ? 'granted' : 'denied'})` : 'Matches role'}
                              >
                                <button
                                  type="button"
                                  onClick={() => toggle(modKey, action)}
                                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 flex-shrink-0 ${
                                    isGranted ? 'bg-gradient-to-r from-navy-800 to-navy-700' : 'bg-gray-300'
                                  }`}
                                  role="switch"
                                  aria-checked={isGranted}
                                >
                                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${isGranted ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                </button>
                                <ActionIcon className={`w-4 h-4 ${isGranted ? 'text-emerald-700' : 'text-gray-400'}`} />
                                <span className={`text-sm font-medium ${isGranted ? 'text-emerald-700' : 'text-gray-700'}`}>{conf.label}</span>
                              </label>
                            );
                          };

                          const renderActionGroup = (groupKey) => {
                            const actions = byGroup[groupKey];
                            if (!actions || actions.length === 0) return null;
                            return (
                              <div key={groupKey} className="space-y-2">
                                {hasExtras && (
                                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                    {PERM_GROUP_LABEL[groupKey]}
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-2">
                                  {actions.map(renderToggle)}
                                </div>
                              </div>
                            );
                          };

                          // Per-module master switch — only for multi-module parents, since
                          // single-module sections are already covered by the section header.
                          const allGranted = modActions.length > 0 && modActions.every(a => getValue(modKey, a));

                          return (
                            <div key={modKey} className="px-4 sm:px-6 py-4 hover:bg-emerald-50/20 transition-colors">
                              {isMulti && (
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <ModIcon className="w-4 h-4 text-gray-500" />
                                  </div>
                                  <span className="font-medium text-gray-700 flex-1 leading-tight break-words" title={modConf.label}>{modConf.label}</span>
                                  <button
                                    type="button"
                                    onClick={() => bulkToggle(modKey, !allGranted)}
                                    title={allGranted ? 'Deny all in this item' : 'Grant all in this item'}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 flex-shrink-0 ${
                                      allGranted ? 'bg-gradient-to-r from-navy-800 to-navy-700' : 'bg-gray-300'
                                    }`}
                                    role="switch"
                                    aria-checked={allGranted}
                                  >
                                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                                      allGranted ? 'translate-x-5' : 'translate-x-0.5'
                                    }`} />
                                  </button>
                                </div>
                              )}
                              <div className="space-y-3">
                                {renderActionGroup('base')}
                                {renderActionGroup('row')}
                                {renderActionGroup('tab')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full ring-2 ring-amber-400 ring-offset-1 bg-gray-200 inline-block" /> Overridden from role</span>
            {overrideCount > 0 && (
              <button onClick={handleReset} className="text-red-500 hover:text-red-600 font-medium underline">Reset to role defaults</button>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-5 py-2.5 text-gray-700 hover:bg-gray-200 rounded-lg font-medium">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <RefreshCw className="h-4 w-4 animate-spin" />}
              <span>{saving ? 'Saving...' : 'Save Permissions'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
